import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  gstAppliesToDocumentFor,
  isPreGstRegistrationFor,
} from "../../../../shared/utils/gstApplicability";
import { GstPreRegistrationNotice } from "../../../../shared/presentation/components/GstPreRegistrationNotice";
import { LocationPicker } from "../../../../shared/presentation/components/LocationPicker";
import { useActiveLocations } from "../../../../shared/hooks/useActiveLocations";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import {
  useToast,
  ErrorBanner,
} from "../../../../shared/presentation/components";
import { useErrorHandling } from "../../../../shared/presentation/hooks";
import { Alert } from "../../../../ui/components/Alert";
import { Loader } from "../../../../ui/components/Loader";
import { Breadcrumbs } from "../../../../ui/components/Breadcrumbs";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { TextField } from "../../../../ui/components/TextField";
import { Badge } from "../../../../ui/components/Badge";
import { Tooltip } from "../../../../ui/components/Tooltip";
import { StatusBadgeForCreditAndDebitNotes } from "../../../../ui/components/StatusBadgeForCreditAndDebitNotes";
import {
  FeatherX,
  FeatherSave,
  FeatherCheck,
  FeatherCalendar,
  FeatherPlus,
  FeatherChevronDown,
  FeatherAlertTriangle,
  FeatherEdit2,
  FeatherLock,
  FeatherMessageCircle,
  FeatherArrowLeft,
} from "@subframe/core";
import { EditLineItemForDebitNoteComponent } from "../components";
import DocumentViewer from "../../../../components/DocumentViewer";
import QueryRaiseModal from "../../../../shared/queries/presentation/components/QueryRaiseModal";
import QueryDetailModal from "../../../../shared/queries/presentation/components/QueryDetailModal";
import QueriesListForDocument from "../../../../shared/queries/presentation/components/QueriesListForDocument";
import ActivityLogComponent from "../../../../components/ActivityLogComponent";
import { SearchableDropdown } from "../../../../shared/presentation/components";
import {
  fetchCurrencies,
  Currency,
} from "../../../../shared/data/api/currenciesApi";
import { getCompany } from "../../../../shared/data/storage/authStorage";
import {
  getApproveAndPostQueriesMessage,
  getFailedUpdateInBackendMessage,
  getPushPermissionDeniedMessage,
  getReviewRequestPushReason,
  getReviewRequestUpdateAfterPostReason,
  getStoredAccountingBackend,
  getUpdateAfterPostPermissionDeniedMessage,
  getUpdateButtonLabel,
  getUpdatedInBackendSuccessMessage,
} from "../../../../config/accounting";
import { useDebitNoteReview } from "../hooks/useDebitNoteReview";
import { useDebitNoteOperations } from "../hooks/useDebitNoteOperations";
import {
  useCustomersForDebitNote,
  CustomerListItem,
} from "../hooks/useCustomersForDebitNote";
import { useCustomerInvoices } from "../hooks/useCustomerInvoices";
import { getCustomerById } from "../../../../services/customerApi";
import { useZohoInvoiceDetails } from "../hooks/useZohoInvoiceDetails";
import { useQueriesBySource } from "../../../../shared/queries/presentation/hooks/useQueriesBySource";
import {
  DebitNoteDataEntity,
  DebitNoteLineItemEntity,
  DebitNoteEntity,
} from "../../domain/entities/DebitNoteEntity";
import { InvoiceListItemEntity } from "../../../../shared/domain/entities/InvoiceListItemEntity";

// Permissions and Review
import {
  MODULE_CODES,
  ACTION_CODES,
  type ActionCode,
} from "../../../../shared/constants/permissions";
import { ReviewRequestModal } from "../../../../shared/presentation/components/ReviewRequestModal";
import {
  ReviewStatusBanner,
  ClosedMonthBanner,
  ClosedMonthConfirmModal,
} from "../../../../shared/presentation/components";
import { useEntityReviewStatus } from "../../../../shared/presentation/hooks/useEntityReviewStatus";
import { useCreditDebitNoteReviewData } from "../../../../shared/presentation/hooks";
import {
  useIsMonthClosed,
  useIsAdminOrCFO,
} from "../../../../shared/presentation/hooks";
import {
  ChangeDetails,
  ACTION_TYPE_MONTH_CLOSE,
} from "../../../../shared/utils/reviewWorkflow";
import { dateToMonthYear } from "../../../../shared/utils/date";
import { DebitNoteRepositoryImpl } from "../../data/repositories/DebitNoteRepositoryImpl";
import { useAuth } from "../../../../features/auth/presentation/hooks/useAuth";

/**
 * ============================================================================
 * PERMISSION CHECKS DOCUMENTATION
 * ============================================================================
 *
 * Screen: Debit Note Review/Detail Page
 * File: frontend/src/features/debit/presentation/pages/DebitNoteReviewPage.tsx
 *
 * API Endpoints Used:
 * 1. GET /api/permissions/me
 *    - Purpose: Fetch all user permissions
 *    - Called: Once on component mount via usePermissions hook
 *
 * 2. POST /api/reviews (if update_after_push requires review)
 *    - Purpose: Create review request for updating posted debit note
 *    - Called: When user tries to edit a posted debit note and review is required
 *
 * 3. GET /api/reviews/check-approved (if update_after_push requires review)
 *    - Purpose: Check if update_after_push has approved review
 *    - Called: Before allowing edit when debit note is posted and review required
 *
 * Request Body: N/A (GET request for permissions)
 *
 * Response Structure for GET /api/permissions/me:
 * {
 *   "success": true,
 *   "data": {
 *     "permissions": {
 *       "debit_note": {
 *         "create": { "granted": true, "requires_review": false },
 *         "update": { "granted": true, "requires_review": false },
 *         "update_after_push": { "granted": true, "requires_review": true }
 *       }
 *     }
 *   },
 *   "message": "Permissions retrieved successfully"
 * }
 *
 * Interface Used:
 * - PermissionsMap from frontend/src/features/admin/domain/entities/PermissionEntity.ts
 * - CreateReviewRequestDTO from frontend/src/shared/utils/reviewWorkflow.ts
 * - CreateReviewResponseDTO from frontend/src/shared/utils/reviewWorkflow.ts
 * - CheckApprovedReviewResponseDTO from frontend/src/shared/utils/reviewWorkflow.ts
 *
 * Permission Checks Implemented:
 * 1. debit_note.create
 *    - Checked via: hasPermission(MODULE_CODES.DEBIT_NOTE, ACTION_CODES.CREATE)
 *    - Used for: Manual creation (when isManualCreation === true)
 *    - Location: Save/Create button when creating new debit note
 *
 * 2. debit_note.update
 *    - Checked via: getPermissionInfo(MODULE_CODES.DEBIT_NOTE, ACTION_CODES.UPDATE)
 *    - Used for: Edit button, auto-save, "Save and Go Back" button (when NOT posted)
 *
 * 3. debit_note.update_after_push
 *    - Checked via: getPermissionInfo(MODULE_CODES.DEBIT_NOTE, ACTION_CODES.UPDATE_AFTER_PUSH)
 *    - Used for: Edit button, auto-save, "Save and Go Back" button (when IS posted)
 *    - Review Required: Yes (if requires_review is true)
 *
 * Review Workflow: Same as InvoiceReviewPage
 * ============================================================================
 */

// Countries data - Import from AddCustomerPage pattern
import { COUNTRIES, findCountryByCode } from "../../../../shared/constants/countries";
import { INDIAN_STATES, findIndianStateByCode } from "../../../../shared/constants/indianStates";

// Create repository instance for document fetching
const debitNoteRepository = new DebitNoteRepositoryImpl();

interface DebitNoteReviewPageProps {}

const omitSupplyFields = <T extends Record<string, any> | null | undefined>(
  data: T,
): T => {
  if (!data) return data;
  const {
    place_of_supply: _placeOfSupply,
    destination_of_supply: _destinationOfSupply,
    source_of_supply: _sourceOfSupply,
    ...rest
  } = data;
  return rest as T;
};

const DebitNoteReviewPage: React.FC<DebitNoteReviewPageProps> = () => {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const accountingBackend = getStoredAccountingBackend();

  // Extract data from navigation state
  const documentId = location.state?.documentId || id || null;
  const isManualCreation = location.state?.isManualCreation || !documentId;
  const isEditMode = location.state?.isEdit || false;
  const initialSignedUrl: string | null = location.state?.signedUrl || null;
  const initialFileName = location.state?.fileName || null;
  const initialDebitNoteJson: DebitNoteDataEntity | null =
    location.state?.debitNoteJson || null;
  const initialDebitNoteDocument: DebitNoteEntity | null =
    location.state?.debitNoteDocument || null;

  // Fetch debit note document function for the hook
  const fetchDebitNoteDocument = useCallback(
    async (id: string): Promise<DebitNoteEntity> => {
      return await debitNoteRepository.getDebitNoteById(parseInt(id));
    },
    []
  );

  // Check if debit note is posted to Zoho
  const checkDebitNotePostedToZoho = useCallback(
    (doc: DebitNoteEntity): boolean => {
      return (
        doc.status === "1" &&
        doc.posted_to_zoho_date !== null &&
        doc.posted_to_zoho_date !== undefined
      );
    },
    []
  );

  // Use the new review data hook
  const reviewData = useCreditDebitNoteReviewData({
    moduleCode: MODULE_CODES.DEBIT_NOTE,
    entityType: "debit_note",
    documentId,
    fetchDocument: fetchDebitNoteDocument,
    isPostedToZoho: checkDebitNotePostedToZoho,
    initialDocument: initialDebitNoteDocument,
    includeDeleteAfterPush: false, // Debit notes don't have delete_after_push
  });

  // Extract values from reviewData
  const {
    isDataLoading,
    document: debitNoteDocumentFromHook,
    isPostedToZoho,
    canCreate,
    canUpdate,
    canUpdateAfterPush,
    canPushToZoho,
    needsReviewForCreate,
    needsReviewForUpdate,
    needsReviewForUpdateAfterPush,
    needsReviewForPushToZoho,
    hasApprovedReview,
  } = reviewData;

  // Get debit note document details (for signedUrl, fileName, etc.)
  const debitNoteDocument = debitNoteDocumentFromHook as DebitNoteEntity | null;
  const signedUrl = debitNoteDocument?.signed_url || initialSignedUrl;
  const fileName = debitNoteDocument?.original_filename || initialFileName;
  const fetchedDebitNoteJson = debitNoteDocument?.json || null;

  // Store the original document when first loaded (for comparison)
  const [originalDebitNoteJson, setOriginalDebitNoteJson] =
    useState<DebitNoteDataEntity | null>(initialDebitNoteJson);

  // Track all changes made by the user (accumulated as they edit)
  const [trackedChanges, setTrackedChanges] = useState<
    Array<{
      field: string;
      old: string;
      new: string;
    }>
  >([]);

  // Helper functions for change tracking (to be used when implementing full change tracking)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _formatValueForTracking = (value: any): string => {
    if (value === null || value === undefined) return "empty";
    if (value === "") return "empty";
    if (typeof value === "object") return JSON.stringify(value);
    if (typeof value === "number") return String(value);
    return String(value);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _formatFieldName = (fieldName: string): string => {
    const formatted = fieldName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
    return formatted;
  };

  /**
   * Build field_changes from current debit note form (for closed-month when trackedChanges is empty).
   * Same pattern as NewJournalEntry.calculateFieldChanges - show form data as summary.
   */
  const getFieldChangesFromDebitNoteForm = useCallback(
    (
      json: DebitNoteDataEntity | null
    ): Array<{ field: string; old: string; new: string }> => {
      const changes: Array<{ field: string; old: string; new: string }> = [];
      if (!json) return changes;
      const toStr = (v: any) =>
        v == null || v === ""
          ? ""
          : typeof v === "object"
          ? JSON.stringify(v)
          : String(v);
      if (json.date)
        changes.push({ field: "Date", old: "", new: toStr(json.date) });
      if (json.customer_name)
        changes.push({
          field: "Customer Name",
          old: "",
          new: toStr(json.customer_name),
        });
      const debitNoteNum = json.debitnote_number ?? json.debit_note_number;
      if (debitNoteNum)
        changes.push({
          field: "Debit Note Number",
          old: "",
          new: toStr(debitNoteNum),
        });
      if (json.gst_treatment)
        changes.push({
          field: "Gst Treatment",
          old: "",
          new: toStr(json.gst_treatment),
        });
      const items = json.line_items || [];
      items.forEach((item: any, index: number) => {
        if (item.quantity != null)
          changes.push({
            field: `line_items[${index}].quantity`,
            old: "",
            new: toStr(item.quantity),
          });
        if (item.rate != null)
          changes.push({
            field: `line_items[${index}].rate`,
            old: "",
            new: toStr(item.rate),
          });
        if (item.name)
          changes.push({
            field: `Line Item ${index + 1}: Name`,
            old: "",
            new: toStr(item.name),
          });
        if (item.description)
          changes.push({
            field: `Line Item ${index + 1}: Description`,
            old: "",
            new: toStr(item.description),
          });
      });
      return changes;
    },
    []
  );

  // Closed month confirm modal (when non-Admin/CFO tries to save or approve in closed month)
  const [showClosedMonthConfirmModal, setShowClosedMonthConfirmModal] =
    useState(false);
  const [pendingClosedMonthSource, setPendingClosedMonthSource] = useState<
    "save" | "approve" | null
  >(null);

  // Review request modal state
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [pendingReviewAction, setPendingReviewAction] = useState<{
    action: () => void;
    changeDetails?: ChangeDetails;
    disableSubmit?: boolean;
    actionCode?: ActionCode;
  } | null>(null);

  // Closed month: derive month from debit note date (synced when debitNoteJson.date changes)
  const [dateForClosedCheck, setDateForClosedCheck] = useState(
    initialDebitNoteJson?.date ?? fetchedDebitNoteJson?.date ?? ""
  );
  const monthForClosedCheck = dateForClosedCheck
    ? dateToMonthYear(dateForClosedCheck)
    : "";
  const { isClosed: isMonthClosed } = useIsMonthClosed(
    monthForClosedCheck || undefined
  );
  const isAdminOrCFO = useIsAdminOrCFO();

  // Review status (for banner display)
  const { review } = useEntityReviewStatus(
    documentId ? "debit_note" : null,
    documentId ? parseInt(documentId) : null
  );

  // Check if review is pending (blocks all actions)
  const isReviewPending = review?.status === "pending";
  const isReviewRejected = review?.status === "rejected";

  // Remove permission checks - always allow editing
  const hasEditPermission = true;
  const shouldDisableEditableFields = false;

  // State for showing approved banner temporarily (auto-dismisses after 5 seconds)
  const [showApprovedBanner, setShowApprovedBanner] = useState(false);
  const approvedBannerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Use review status from reviewData hook
  const isCheckingReview = reviewData.isReviewDataLoading;

  // Show approved banner when review is approved (on mount or when review changes to approved)
  useEffect(() => {
    if (review?.status === "approved") {
      setShowApprovedBanner(true);

      // Clear any existing timeout
      if (approvedBannerTimeoutRef.current) {
        clearTimeout(approvedBannerTimeoutRef.current);
      }

      // Auto-dismiss after 5 seconds
      approvedBannerTimeoutRef.current = setTimeout(() => {
        setShowApprovedBanner(false);
      }, 5000);
    } else {
      setShowApprovedBanner(false);
    }

    // Cleanup timeout on unmount
    return () => {
      if (approvedBannerTimeoutRef.current) {
        clearTimeout(approvedBannerTimeoutRef.current);
      }
    };
  }, [review?.status, review?.id]);

  // ZOUR-101 — same guard as the credit page: an existing note must be IN STATE
  // before anything may auto-save. `debitNoteJson` starts as the blank default,
  // `isPostedToZoho` is false until the GET returns, and the totals effect fires
  // on mount, so a load slower than the 1 s debounce queued the EMPTY note for
  // saving over the real one. `originalDebitNoteJson` flips in the same batch as
  // `setDebitNoteJson`, so there is no render with auto-save on and default state.
  // (Today the debit update-json route does not exist server-side, so the POST
  // would 404 — this keeps the page correct once it does.)
  const isLoadedNoteHydrated =
    !documentId ||
    originalDebitNoteJson !== null ||
    (debitNoteDocument !== null && !debitNoteDocument.json);

  // Remove permission checks - only disable auto-save for posted documents
  const disableAutoSave = isPostedToZoho || !isLoadedNoteHydrated;

  // Initialize debit note review hook (handles JSON state and auto-save)
  const {
    debitNoteJson,
    setDebitNoteJson,
    isAutoSaving,
    lastSaved,
    updateDebitNoteJson,
  } = useDebitNoteReview(
    documentId,
    initialDebitNoteJson,
    disableAutoSave,
    () => isMonthClosed === true && !isAdminOrCFO,
    () => {
      setPendingClosedMonthSource("save");
      setShowClosedMonthConfirmModal(true);
    }
  );

  // Sync date for closed-month check when debit note date changes
  useEffect(() => {
    if (debitNoteJson?.date) {
      setDateForClosedCheck(debitNoteJson.date);
    }
  }, [debitNoteJson?.date]);

  // Enhanced updateDebitNoteJson that tracks changes
  const updateDebitNoteJsonWithTracking = useCallback(
    (updates: Partial<DebitNoteDataEntity>) => {
      // Update the JSON first
      updateDebitNoteJson(updates);

      // Track changes by comparing with original document
      if (originalDebitNoteJson) {
        setTrackedChanges((prevTrackedChanges) => {
          const changesToTrack: Array<{
            field: string;
            old: string;
            new: string;
          }> = [];
          const updatedChanges = [...prevTrackedChanges];

          // Customer-related fields that should be removed when customer_id changes
          const customerAutoUpdateFields = [
            "customer_id",
            "customer_name",
            "gst_no",
          ];

          // Check if customer_id is being changed
          const isCustomerIdChanging =
            updates.customer_id !== undefined &&
            originalDebitNoteJson.customer_id !== updates.customer_id;

          // If customer_id is changing, remove all customer-related tracked changes
          if (isCustomerIdChanging) {
            customerAutoUpdateFields.forEach((field) => {
              const index = updatedChanges.findIndex(
                (change) =>
                  change.field === field ||
                  change.field === _formatFieldName(field)
              );
              if (index >= 0) {
                updatedChanges.splice(index, 1);
              }
            });

            // Track customer change
            const oldCustomerName =
              originalDebitNoteJson.customer_name || "Unknown";
            const newCustomerName =
              (updates.customer_name as string) || "Unknown";

            changesToTrack.push({
              field: "Customer",
              old: oldCustomerName,
              new: newCustomerName,
            });
          }

          // Process all updates
          Object.keys(updates).forEach((key) => {
            // Skip line_items - handled separately in handleSaveLineItem
            if (key === "line_items") {
              return;
            }

            // Skip customer_id if we already handled it above
            if (isCustomerIdChanging && key === "customer_id") {
              return;
            }

            // Skip auto-update fields if customer is changing
            if (
              isCustomerIdChanging &&
              customerAutoUpdateFields.indexOf(key) !== -1
            ) {
              return;
            }

            const oldValue = (originalDebitNoteJson as any)[key];
            const newValue = (updates as any)[key];

            // Only track if value actually changed from original
            if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
              // Format field name for display
              const formattedFieldName = _formatFieldName(key);

              // Check if this field is already tracked
              const existingChangeIndex = updatedChanges.findIndex(
                (change) => change.field === formattedFieldName
              );

              if (existingChangeIndex >= 0) {
                // Update existing tracked change
                updatedChanges[existingChangeIndex] = {
                  field: formattedFieldName,
                  old: _formatValueForTracking(oldValue),
                  new: _formatValueForTracking(newValue),
                };
              } else {
                // Add new tracked change
                changesToTrack.push({
                  field: formattedFieldName,
                  old: _formatValueForTracking(oldValue),
                  new: _formatValueForTracking(newValue),
                });
              }
            } else {
              // If value matches original, remove it from tracked changes (user reverted)
              const formattedFieldName = _formatFieldName(key);
              const existingChangeIndex = updatedChanges.findIndex(
                (change) => change.field === formattedFieldName
              );
              if (existingChangeIndex >= 0) {
                updatedChanges.splice(existingChangeIndex, 1);
              }
            }
          });

          // Return updated array: existing changes (possibly modified) + new changes
          return [...updatedChanges, ...changesToTrack];
        });
      }
    },
    [
      updateDebitNoteJson,
      originalDebitNoteJson,
      _formatValueForTracking,
      _formatFieldName,
    ]
  );

  // Ref to track latest debitNoteJson for review requests (to avoid stale state)
  const debitNoteJsonRef = useRef<DebitNoteDataEntity | null>(
    initialDebitNoteJson
  );
  useEffect(() => {
    debitNoteJsonRef.current = debitNoteJson;
  }, [debitNoteJson]);

  // Update original when document is fetched
  useEffect(() => {
    if (
      fetchedDebitNoteJson &&
      (!originalDebitNoteJson ||
        originalDebitNoteJson.debitnote_number !==
          fetchedDebitNoteJson.debitnote_number)
    ) {
      setOriginalDebitNoteJson(fetchedDebitNoteJson);
      setDebitNoteJson(fetchedDebitNoteJson);
      // Reset tracked changes when new document is loaded
      setTrackedChanges([]);
    }
  }, [fetchedDebitNoteJson, originalDebitNoteJson, setDebitNoteJson]);

  // Initialize debit note operations hook
  const {
    loading: operationsLoading,
    error: operationsError,
    createZohoDebitNote,
    updateZohoDebitNote,
    rejectDebitNote,
    saveDebitNoteAsDraft,
    clearError: clearOperationsError,
  } = useDebitNoteOperations();

  // Zoho invoice details hook for fetching invoice line items
  const {
    invoiceDetails,
    loading: invoiceDetailsLoading,
    error: invoiceDetailsError,
    fetchInvoiceDetails,
    clearError: clearInvoiceDetailsError,
    reset: resetInvoiceDetails,
  } = useZohoInvoiceDetails();

  // Initialize customer hooks
  const { fetchCustomers: originalFetchCustomers, loading: customersLoading } =
    useCustomersForDebitNote();

  // Cache for selected customer to push to top
  const selectedCustomerCacheRef = useRef<CustomerListItem | null>(null);

  // Cache for selected invoice to push to top
  const selectedInvoiceCacheRef = useRef<InvoiceListItemEntity | null>(null);

  // Wrapper for fetchCustomers that pushes selected customer to top
  const fetchCustomers = useCallback(
    async (
      searchTerm: string,
      limit: number,
      offset: number,
      selectedCustomerId?: string,
      selectedCustomerName?: string
    ) => {
      const result = await originalFetchCustomers(searchTerm, limit, offset);

      // Always ensure selected customer is in results (if provided)
      if (
        selectedCustomerId &&
        selectedCustomerName &&
        offset === 0 &&
        !searchTerm
      ) {
        const selectedKey = selectedCustomerId;

        // Check if selected customer is already in the results
        const selectedIndex = result.items.findIndex(
          (item) =>
            (item.zoho_customerid || item.customer_id || "") === selectedKey
        );

        if (selectedIndex >= 0) {
          // Selected customer is in results, move it to top
          if (selectedIndex > 0) {
            const itemsCopy = [...result.items];
            const [selectedItem] = itemsCopy.splice(selectedIndex, 1);
            return {
              items: [selectedItem, ...itemsCopy],
              hasMore: result.hasMore,
            };
          }
          // Already at top, return as is
          return result;
        } else {
          // Selected customer not found in results
          // Check cache first
          const cachedCustomer = selectedCustomerCacheRef.current;
          if (cachedCustomer) {
            const cachedKey =
              cachedCustomer.zoho_customerid ||
              cachedCustomer.customer_id ||
              "";
            if (cachedKey === selectedKey) {
              return {
                items: [cachedCustomer, ...result.items],
                hasMore: result.hasMore,
              };
            }
          }

          // Try to fetch from backend
          try {
            const response = await getCustomerById(selectedCustomerId);
            if (response.data) {
              const fetchedCustomer: CustomerListItem = {
                id: 0, // API doesn't return id
                zoho_customerid: response.data.zoho_customerid || "",
                customer_id: response.data.customer_id || "",
                customer_name: response.data.customer_name || "",
                company_name: response.data.customer_name || "", // API doesn't have company_name, use customer_name
                email: response.data.contact_email_1 || "",
                phone: response.data.contact_number_1 || "",
                status: response.data.status || "",
                billing_address: response.data.billing_address || null,
                shipping_address: response.data.shipping_address || null,
                currency_code: response.data.currency_code ?? response.data.currency_id ?? "",
                currency_symbol: response.data.currency_symbol ?? "",
                payment_terms: response.data.payment_terms || "",
                credit_limit: response.data.credit_limit || 0,
                created_at: response.data.created_at || "",
                updated_at: response.data.updated_at || "",
              };
              // Store in cache
              selectedCustomerCacheRef.current = fetchedCustomer;
              return {
                items: [fetchedCustomer, ...result.items],
                hasMore: result.hasMore,
              };
            }
          } catch (error) {
            console.warn(
              "Could not fetch selected customer from backend, using synthetic item:",
              error
            );
          }

          // Fallback: Create synthetic customer
          const syntheticCustomer: CustomerListItem = {
            id: 0,
            zoho_customerid: selectedCustomerId,
            customer_id: selectedCustomerId,
            customer_name: selectedCustomerName,
            company_name: selectedCustomerName,
            email: "",
            phone: "",
            status: "",
            billing_address: null,
            shipping_address: null,
            currency_code: "",
            currency_symbol: "",
            payment_terms: "",
            credit_limit: 0,
            created_at: "",
            updated_at: "",
          };
          return {
            items: [syntheticCustomer, ...result.items],
            hasMore: result.hasMore,
          };
        }
      }

      return result;
    },
    [originalFetchCustomers]
  );

  const { fetchInvoices: originalFetchInvoices, loading: invoicesLoading } =
    useCustomerInvoices();

  // Wrapper for fetchInvoices that pushes selected invoice to top
  const fetchInvoices = useCallback(
    async (
      customerZohoId: string,
      searchTerm: string,
      limit: number,
      offset: number,
      selectedInvoiceId?: string,
      selectedInvoiceNumber?: string
    ) => {
      const result = await originalFetchInvoices(
        customerZohoId,
        searchTerm,
        limit,
        offset
      );

      // Always ensure selected invoice is in results (if provided)
      if (
        selectedInvoiceId &&
        selectedInvoiceNumber &&
        offset === 0 &&
        !searchTerm
      ) {
        const selectedKey = selectedInvoiceId;

        // Check if selected invoice is already in the results
        const selectedIndex = result.items.findIndex(
          (item) =>
            (item.invoice_id || item.id?.toString() || "") === selectedKey
        );

        if (selectedIndex >= 0) {
          // Selected invoice is in results, move it to top
          if (selectedIndex > 0) {
            const itemsCopy = [...result.items];
            const [selectedItem] = itemsCopy.splice(selectedIndex, 1);
            return {
              items: [selectedItem, ...itemsCopy],
              hasMore: result.hasMore,
            };
          }
          // Already at top, return as is
          return result;
        } else {
          // Selected invoice not found in results
          // Check cache first
          const cachedInvoice = selectedInvoiceCacheRef.current;
          if (cachedInvoice) {
            const cachedKey =
              cachedInvoice.invoice_id || cachedInvoice.id?.toString() || "";
            if (cachedKey === selectedKey) {
              return {
                items: [cachedInvoice, ...result.items],
                hasMore: result.hasMore,
              };
            }
          }

          // Try to fetch invoice details from Zoho API
          try {
            // Fetch invoice details - this will automatically set invoiceDetails state
            // which will trigger the useEffect to process line items automatically
            await fetchInvoiceDetails(selectedInvoiceId);
            // Note: invoiceDetails state will be updated by fetchInvoiceDetails,
            // and the useEffect will automatically process line items when state updates.
            // For the dropdown, we'll create a synthetic item since we can't access
            // the state immediately (React state updates are async)
          } catch (error) {
            console.warn(
              "Could not fetch selected invoice from Zoho API, using synthetic item:",
              error
            );
          }

          // Fallback: Create synthetic invoice
          const syntheticInvoice: InvoiceListItemEntity = {
            invoice_id: selectedInvoiceId,
            id: parseInt(selectedInvoiceId) || null,
            invoice_number: selectedInvoiceNumber,
            date: "",
            total: 0,
            tax_total: 0,
            balance: 0,
            unprocessed_payment_amount: 0,
            currency_id: "",
            exchange_rate: 0,
            currency_code: "",
            currency_symbol: "",
            is_tds_applied: 0,
            tds_tax_id: null,
            tds_section: null,
            tds_tax_amount: 0,
            tds_tax_percentage: 0,
            tds_tax_name: null,
          };
          return {
            items: [syntheticInvoice, ...result.items],
            hasMore: result.hasMore,
          };
        }
      }

      return result;
    },
    [originalFetchInvoices, fetchInvoiceDetails]
  );

  // Clean Architecture: Use queries hook
  const {
    queries: queriesEntities,
    loading: queriesLoading,
    loadingMore: queriesLoadingMore,
    refetch: refetchQueries,
    hasMore: queriesHasMore,
    loadMore: loadMoreQueries,
  } = useQueriesBySource(
    "debit_note",
    documentId ? parseInt(documentId) : undefined
  );

  // Currency state
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [currenciesLoading, setCurrenciesLoading] = useState<boolean>(false);
  const [currenciesError, setCurrenciesError] = useState<string | null>(null);

  // Error handling
  const { errors, setFieldErrors, setApiErrors, clearErrors, clearFieldError } =
    useErrorHandling();

  // UI State
  const [status, setStatus] = useState<
    "pending" | "approved" | "rejected" | "draft"
  >(isManualCreation ? "draft" : "pending");
  // For manual creation, always allow editing. For existing documents, check status.
  const [isReadOnly, setIsReadOnly] = useState<boolean>(
    isManualCreation ? false : false // Start as false, will be set based on document status
  );

  // When posted, customer and all other fields should be disabled
  // Line items (quantity/rate) remain editable
  const isCustomerVendorDisabled = isReadOnly || isPostedToZoho;
  const isOtherFieldsDisabled = isReadOnly || isPostedToZoho;
  // Line items remain editable (don't include isPostedToZoho here)

  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Display errors from hooks in ErrorBanner
  useEffect(() => {
    if (operationsError) {
      setApiErrors(new Error(operationsError));
    }
  }, [operationsError, setApiErrors]);

  // Line item editing state
  const [editingLineItemIndex, setEditingLineItemIndex] = useState<
    number | null
  >(null);
  const [editingLineItemData, setEditingLineItemData] =
    useState<DebitNoteLineItemEntity | null>(null);
  const [originalAccountName, setOriginalAccountName] = useState<string>("");
  const [isEditLineItemModalOpen, setIsEditLineItemModalOpen] =
    useState<boolean>(false);

  // Query and activity log state
  const [isQueryRaiseModalOpen, setIsQueryRaiseModalOpen] = useState(false);
  const [isQueryDetailModalOpen, setIsQueryDetailModalOpen] = useState(false);
  const [selectedQueryId, setSelectedQueryId] = useState<number | undefined>();
  const [activityLogRefreshKey, setActivityLogRefreshKey] = useState(0);

  // Selected customer and invoice
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [selectedCustomerName, setSelectedCustomerName] = useState<string>("");
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>("");
  const [selectedInvoiceNumber, setSelectedInvoiceNumber] =
    useState<string>("");

  // Store invoice date for validation
  const [linkedInvoiceDate, setLinkedInvoiceDate] = useState<string>("");

  // Ref for error banner to scroll to on validation errors
  const errorBannerRef = useRef<HTMLDivElement>(null);

  // Round amount to 2 decimal places
  const roundToTwoDecimals = useCallback((amount: number): number => {
    return Math.round(amount * 100) / 100;
  }, []);

  // Process invoice details when fetched and map to debit note line items
  useEffect(() => {
    if (invoiceDetails && invoiceDetails.line_items) {
      // Avoid overwriting saved drafts or posted notes with fetched invoice lines
      const statusValue = Number(debitNoteDocument?.status);
      const isDraftStatus = statusValue === 0 || statusValue === 4;
      const hasSavedLines = (initialDebitNoteJson?.line_items?.length || 0) > 0;
      const skipImportFromInvoice =
        hasSavedLines && (isDraftStatus || isPostedToZoho);
      if (skipImportFromInvoice) {
        return;
      }
      const zohoInvoiceId =
        invoiceDetails.invoice_id ||
        debitNoteJson?.zoho_invoiceid ||
        debitNoteJson?.linked_invoice_id ||
        "";

      // Map invoice line items to debit note line items
      // Exclude line_item_id as per requirements
      const mappedLineItems: DebitNoteLineItemEntity[] =
        invoiceDetails.line_items.map((item: any) => {
          // Calculate item_total and tax_amount manually for consistency (rounded to 2 decimals)
          const quantity = item.quantity || 0;
          const rate = item.rate || 0;
          const itemSubTotal = roundToTwoDecimals(quantity * rate);
          const taxPercentage = item.tax_percentage || 0;
          const taxAmount = roundToTwoDecimals(
            (itemSubTotal * taxPercentage) / 100
          );

          const debitNoteLineItem: DebitNoteLineItemEntity = {
            description: item.description || item.name || "",
            quantity: quantity,
            rate: rate,
            account_id: item.account_id || "",
            account_name: item.account_name || "",
            hsn_or_sac: item.hsn_or_sac || "",
            tax_id: item.tax_id || "",
            tax_name: item.tax_name || "",
            tax_percentage: taxPercentage,
            tax_amount: taxAmount,
            item_total: itemSubTotal,
            invoice_id: zohoInvoiceId, // Link to invoice
            type: 1, // Debit note line item type
          };

          // Add TDS fields if present
          if (item.tds_tax_id) {
            debitNoteLineItem.tds_tax_id = item.tds_tax_id;
          }
          if (item.tds_tax_percentage) {
            debitNoteLineItem.tds_tax_percentage = item.tds_tax_percentage;
          }

          // Explicitly exclude line_item_id - do not include it
          // Return only the fields we want

          return debitNoteLineItem;
        });

      // Update debit note JSON with mapped line items
      const invoiceId =
        invoiceDetails.invoice_id || debitNoteJson?.linked_invoice_id || "";
      const invoiceNumber =
        invoiceDetails.invoice_number || debitNoteJson?.reference_number || "";
      const invoiceDate = invoiceDetails.date || "";

      // ZOUR-65 — the note takes the PARENT's registration here too.
      //
      // `NoteParentLocationResolver::forDebitNote()` resolves the parent INVOICE
      // (`reference_invoice_id` / `invoice_id`) and `enforce()` overwrites the
      // note's location with it before the push, so Zoho was already right. This
      // is the screen catching up: without it the note's `location_id` stayed
      // NULL, which the locked picker prints as the primary while the caption
      // says the note follows an invoice raised somewhere else.
      //
      // NULL stays NULL — a parent with no registration of its own is on the
      // primary, and inheriting NULL is what the backend does too.
      const updates: any = {
        linked_invoice_id: invoiceId,
        zoho_invoiceid: zohoInvoiceId,
        invoiceId: zohoInvoiceId,
        reference_number: invoiceNumber,
        location_id: invoiceDetails.location_id ?? null,
        line_items: mappedLineItems,
      };

      // Set the date from invoice if available (always auto-populate)
      if (invoiceDate) {
        setLinkedInvoiceDate(invoiceDate);
        // Always auto-populate date from invoice date
        updates.date = invoiceDate;
      }

      updateDebitNoteJson(updates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceDetails, roundToTwoDecimals]);

  // Initialize and sync local state from JSON
  useEffect(() => {
    if (debitNoteJson) {
      // Sync local state from JSON - Customer data
      if (debitNoteJson.customer_name) {
        setSelectedCustomerName(debitNoteJson.customer_name);
      }
      if (debitNoteJson.customer_id) {
        setSelectedCustomerId(debitNoteJson.customer_id);
      }
      // Pre-populate invoice dropdown if linked_invoice_id or zoho_invoiceid exists
      if (debitNoteJson.linked_invoice_id || debitNoteJson.zoho_invoiceid) {
        const invoiceId =
          debitNoteJson.linked_invoice_id || debitNoteJson.zoho_invoiceid || "";
        const invoiceNumber = debitNoteJson.reference_number || "";

        setSelectedInvoiceId(invoiceId);
        setSelectedInvoiceNumber(invoiceNumber);
      }

      // Auto-populate country/state from JSON for existing documents
      // Note: For manual creation, these remain empty and user must select
      if (!isManualCreation && documentId) {
        // Country and state are already in debitNoteJson if they exist
        // They will be displayed in the dropdowns automatically
      }
    }
  }, [debitNoteJson, isManualCreation, initialDebitNoteJson, documentId]);

  // GST enabled state
  const [isGstEnabled, setIsGstEnabled] = useState(true);

  // ZOUR-19: does GST apply to this debit note?
  //
  // The parent invoice's date decides it — a note adjusts a supply, so what
  // matters is whether GST applied to THAT supply, not to the note's own date.
  //
  // THE SERVER'S ANSWER WINS (review 12). Resolving the parent in the browser
  // was wrong: it could only look at what the invoice picker had loaded, which
  // is empty for a note created through the API or simply not re-selected. The
  // page then fell back to the note's own date and drew 18% tax on a note the
  // server had already pushed to Zoho with none. `debitNoteDocument.gst` comes
  // from the note GET and is produced by the same GstNoteApplicability the push
  // uses, so the two cannot disagree.
  //
  // The client-side chain below is the fallback for a note opened from router
  // state (no GET, so no `gst` block) — and it is only ever a display fallback:
  // the server still decides what reaches the books.
  const serverGstDecision = debitNoteDocument?.gst;

  useEffect(() => {
    if (serverGstDecision) {
      setIsGstEnabled(serverGstDecision.applies);

      return;
    }

    // ZOUR-92: per registration too — see the credit-note twin.
    setIsGstEnabled(
      gstAppliesToDocumentFor(
        linkedInvoiceDate || invoiceDetails?.date || debitNoteJson?.date || "",
        debitNoteJson?.location_id ?? null
      )
    );
  }, [
    serverGstDecision,
    linkedInvoiceDate,
    invoiceDetails?.date,
    debitNoteJson?.date,
    debitNoteJson?.location_id,
  ]);

  // ZOUR-19: the same question, as a value the JSX can read, so the page can
  // SAY why the tax fields are gone. The notice is derived from the date in
  // front of the user rather than from validation results, so it is right
  // immediately and right on a document that has never been validated.
  // ZOUR-65: the company's GST registrations.
  const {
    locations: activeLocations,
    hasMultiple: hasMultipleLocations,
  } = useActiveLocations();

  /**
   * ZOUR-65 — does this note hang off a parent invoice?
   *
   * With a parent, the note inherits that invoice's GST registration and the
   * picker is read-only: Zoho accepts a note on a different location silently
   * and splits the tax the other way, so the server forces the parent's before
   * pushing and this screen only reports it.
   *
   * Standalone, there is nothing to inherit — without a picker the note could
   * only ever land on the primary location — so it is editable.
   */
  const noteHasParent = useMemo(() => {
    const candidates = [
      debitNoteJson?.reference_invoice_id,
      debitNoteJson?.invoice_id,
      debitNoteJson?.linked_invoice_id,
      debitNoteJson?.zoho_invoiceid,
      ...(debitNoteJson?.line_items ?? []).map((line: any) => line?.invoice_id),
    ];

    return candidates.some(
      (value) => typeof value === "string" && value.trim() !== ""
    );
  }, [debitNoteJson]);

  const showPreGstNotice = serverGstDecision
    ? serverGstDecision.is_pre_registration
    : isPreGstRegistrationFor(
        linkedInvoiceDate || invoiceDetails?.date || debitNoteJson?.date || "",
        debitNoteJson?.location_id ?? null
      );

  // Derived values from debitNoteJson - use useMemo to fix dependency warnings
  const lineItems = useMemo(
    () => debitNoteJson?.line_items || [],
    [debitNoteJson?.line_items]
  );

  // Load currencies
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        setCurrenciesLoading(true);
        setCurrenciesError(null);
        const response = await fetchCurrencies();
        setCurrencies(response.data);
      } catch (err: any) {
        console.error("Error fetching currencies:", err);
        setCurrenciesError(err.message || "Failed to load currencies");
      } finally {
        setCurrenciesLoading(false);
      }
    };

    loadCurrencies();
  }, []);

  // Initial data loading effect - use document from hook
  useEffect(() => {
    if (isManualCreation || !documentId) {
      setIsInitialLoading(false);
      // For manual creation, set draft status (which is under review)
      setStatus("draft");
      return;
    }

    if (isDataLoading) {
      // Still loading from hook, wait
      return;
    }

    setIsInitialLoading(false);
    setError(null);

    // Document is now fetched by the hook, use it for status mapping
    if (debitNoteDocument) {
      // Map status: 0 = draft/pending, 1 = completed/approved, 2 = under review, 3 = rejected, 4 = draft
      // Draft (0 or 4) and under review (2) should show as "pending"
      const statusValue = Number(debitNoteDocument.status);
      if (statusValue === 0 || statusValue === 2 || statusValue === 4) {
        setStatus("pending"); // Draft and under review both show as pending
      } else if (statusValue === 1) {
        setStatus("approved");
      } else if (statusValue === 3) {
        setStatus("rejected");
      } else {
        setStatus("pending");
      }

      // Set read-only for rejected; otherwise allow editing (closed-month handling is separate)
      if (!isManualCreation) {
        setIsReadOnly(statusValue === 3);
      } else {
        setIsReadOnly(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    documentId,
    isManualCreation,
    isEditMode,
    debitNoteDocument,
    isDataLoading,
  ]);

  // Helper function to find currency by ID
  const findCurrencyById = useCallback(
    (id: string) => {
      return currencies.find((currency) => currency.currency_id === id);
    },
    [currencies]
  );

  // Helper function to handle currency selection
  const handleCurrencyChange = useCallback(
    (currencyId: string) => {
      const selectedCurrency = findCurrencyById(currencyId);
      if (selectedCurrency) {
        updateDebitNoteJsonWithTracking({
          currency_id: selectedCurrency.currency_id,
          currency_code: selectedCurrency.currency_code,
          currency_symbol: selectedCurrency.currency_symbol,
        });
      }
    },
    [findCurrencyById, updateDebitNoteJsonWithTracking]
  );

  // Sync currency state from debitNoteJson - no effect needed, currency is in JSON

  // Calculate totals from line items and update JSON
  // Formula:
  // 1. Subtotal = sum of (qty * rate) for all line items
  // 2. Tax/GST = sum of tax_amount for all line items (calculated from tax_percentage if needed)
  // 3. Total = Subtotal + Tax
  useEffect(() => {
    if (!debitNoteJson) return;

    let calculatedSubTotal = 0;
    let calculatedTaxTotal = 0;

    lineItems.forEach((item) => {
      // 1. Calculate subtotal: qty * rate (excluding tax)
      const quantity = item.quantity || 0;
      const rate = item.rate || 0;
      const itemSubTotal = quantity * rate;
      calculatedSubTotal += itemSubTotal;

      // 2. Calculate tax amount for this line item
      // Use stored tax_amount if available, otherwise calculate from tax_percentage
      let itemTaxAmount = 0;
      if (item.tax_amount !== undefined && item.tax_amount !== null) {
        itemTaxAmount = item.tax_amount;
      } else if (item.tax_percentage) {
        itemTaxAmount = (itemSubTotal * item.tax_percentage) / 100;
      }

      calculatedTaxTotal += itemTaxAmount;
    });

    // Round all totals to 2 decimal places
    calculatedSubTotal = roundToTwoDecimals(calculatedSubTotal);
    calculatedTaxTotal = roundToTwoDecimals(calculatedTaxTotal);

    // 3. Calculate total: subtotal + tax (already rounded values)
    const calculatedTotal = roundToTwoDecimals(
      calculatedSubTotal + calculatedTaxTotal
    );

    // Update JSON if totals changed
    if (
      debitNoteJson.sub_total !== calculatedSubTotal ||
      debitNoteJson.tax_total !== calculatedTaxTotal ||
      debitNoteJson.tax_amount !== calculatedTaxTotal ||
      debitNoteJson.total !== calculatedTotal
    ) {
      updateDebitNoteJson({
        sub_total: calculatedSubTotal,
        tax_total: calculatedTaxTotal,
        tax_amount: calculatedTaxTotal,
        total: calculatedTotal,
      });
    }
  }, [lineItems, debitNoteJson, updateDebitNoteJson, roundToTwoDecimals]);

  // Validation function
  const validateDebitNote = useCallback((): boolean => {
    // Clear all previous validation errors first
    clearErrors();

    const newErrors: Record<string, string> = {};

    // Required field validations
    // Note: debitnote_number and reference_number are auto-generated
    if (!debitNoteJson?.date?.trim()) {
      newErrors.date = "Date is required";
    } else if (linkedInvoiceDate) {
      // Validate that debit note date is not before invoice date
      const debitNoteDate = new Date(debitNoteJson.date);
      const invoiceDate = new Date(linkedInvoiceDate);
      if (debitNoteDate < invoiceDate) {
        newErrors.date = `Debit note date cannot be before the invoice date (${linkedInvoiceDate})`;
      }
    }
    if (!debitNoteJson?.currency_id) {
      newErrors.currency_id = "Currency is required";
    }
    if (!debitNoteJson?.country?.trim()) {
      newErrors.country = "Country is required";
    }
    // State is required only if country is India
    if (debitNoteJson?.country === "IN" && !debitNoteJson?.state?.trim()) {
      newErrors.state = "State is required when country is India";
    }
    if (lineItems.length === 0) {
      newErrors.line_items = "At least one line item is required";
    }

    // Customer debit note validations (no vendor support)
    if (!debitNoteJson?.customer_id) {
      newErrors.customer_id = "Customer is required";
    }
    // Linked Invoice is mandatory for debit notes
    if (!debitNoteJson?.linked_invoice_id?.trim()) {
      newErrors.linked_invoice_id = "Linked invoice is required";
    }
    // GSTIN is required only if customer is selected AND has business_gst treatment
    if (
      debitNoteJson?.customer_id &&
      debitNoteJson?.gst_treatment === "business_gst" &&
      !debitNoteJson?.gst_no?.trim()
    ) {
      newErrors.gst_no = "GSTIN is required for Business GST";
    }

    // Validate line items
    lineItems.forEach((item, index) => {
      if (!item.description?.trim()) {
        newErrors[`line_item_${index}_description`] = `Line item ${
          index + 1
        }: Description is required`;
      }
      if (!item.quantity || item.quantity <= 0) {
        newErrors[`line_item_${index}_quantity`] = `Line item ${
          index + 1
        }: Quantity must be greater than 0`;
      }
      if (!item.rate || item.rate <= 0) {
        newErrors[`line_item_${index}_rate`] = `Line item ${
          index + 1
        }: Rate must be greater than 0`;
      }
    });

    // Set all validation errors at once (after clearing)
    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
    }

    return Object.keys(newErrors).length === 0;
  }, [
    debitNoteJson,
    lineItems,
    linkedInvoiceDate,
    clearErrors,
    setFieldErrors,
  ]);

  // Handler functions
  const handleReject = useCallback(async () => {
    if (!documentId) {
      showError("No document ID available");
      return;
    }

    try {
      clearOperationsError();
      const success = await rejectDebitNote(parseInt(documentId));
      if (success) {
        setStatus("rejected");
        showSuccess("Debit note rejected successfully");
        navigate("/dashboard/debit");
      } else {
        showError(operationsError || "Failed to reject debit note");
      }
    } catch (err: any) {
      showError(err.message || "Failed to reject debit note");
    }
  }, [
    documentId,
    rejectDebitNote,
    showSuccess,
    showError,
    navigate,
    clearOperationsError,
    operationsError,
  ]);

  const handleSaveAsDraft = useCallback(async () => {
    // Check permission for create action
    if (!canCreate) {
      showError("You do not have permission to create debit notes");
      return;
    }

    // Run validation first
    if (!validateDebitNote()) {
      // ErrorBanner will display all validation errors
      // Scroll to error banner
      if (errorBannerRef.current) {
        errorBannerRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      return;
    }

    if (!debitNoteJson) {
      showError("No debit note data available");
      return;
    }

    // Check if review is needed for create action
    if (needsReviewForCreate) {
      // Build changeDetails for review
      const changeDetails: ChangeDetails = {
        reason: "Requesting permission to create debit note",
        field_changes: trackedChanges.length > 0 ? trackedChanges : [],
        raw_json: omitSupplyFields(debitNoteJsonRef.current || debitNoteJson),
        action_types: ["create"],
      };

      setPendingReviewAction({
        action: async () => {
          // This will be called after review is approved
          try {
            clearOperationsError();
            // For new debit notes (manual creation), create a draft using saveDebitNoteAsDraft
            if (!documentId || isManualCreation) {
              // Ensure customer_name is included in the JSON
              const draftData = omitSupplyFields({
                ...debitNoteJson,
                ...(selectedCustomerName
                  ? { customer_name: selectedCustomerName }
                  : {}),
              });

              // Create new draft debit note
              const savedDebitNote = await saveDebitNoteAsDraft(draftData);

              if (savedDebitNote) {
                // Navigate to the saved debit note so auto-save can take over
                navigate("/dashboard/debit");
                setStatus("draft"); // UI shows as "pending" (under review)
                showSuccess("Debit note saved as draft successfully");
              } else {
                showError(
                  operationsError || "Failed to save debit note as draft"
                );
              }
            } else {
              // For existing documents, auto-save already handles updates
              // Just show success message
              showSuccess("Debit note saved as draft successfully");
            }
          } catch (err: any) {
            showError(err.message || "Failed to save debit note as draft");
            setApiErrors(err);
          }
        },
        changeDetails,
        disableSubmit: trackedChanges.length === 0,
        actionCode: ACTION_CODES.CREATE,
      });
      setIsReviewModalOpen(true);
      return;
    }

    // No review needed, proceed directly
    try {
      clearOperationsError();
      // For new debit notes (manual creation), create a draft using saveDebitNoteAsDraft
      if (!documentId || isManualCreation) {
        // Ensure customer_name is included in the JSON
        const draftData = omitSupplyFields({
          ...debitNoteJson,
          ...(selectedCustomerName
            ? { customer_name: selectedCustomerName }
            : {}),
        });

        // Create new draft debit note
        const savedDebitNote = await saveDebitNoteAsDraft(draftData);

        if (savedDebitNote) {
          // Navigate to the saved debit note so auto-save can take over
          navigate("/dashboard/debit");
          setStatus("draft"); // UI shows as "pending" (under review)
          showSuccess("Debit note saved as draft successfully");
        } else {
          showError(operationsError || "Failed to save debit note as draft");
        }
      } else {
        // For existing documents, auto-save already handles updates
        // Just show success message
        showSuccess("Debit note saved as draft successfully");
      }
    } catch (err: any) {
      showError(err.message || "Failed to save debit note as draft");
      setApiErrors(err);
    }
  }, [
    canCreate,
    needsReviewForCreate,
    validateDebitNote,
    debitNoteJson,
    trackedChanges,
    debitNoteJsonRef,
    documentId,
    isManualCreation,
    selectedCustomerName,
    saveDebitNoteAsDraft,
    showSuccess,
    showError,
    navigate,
    clearOperationsError,
    operationsError,
    setApiErrors,
  ]);

  // Handle review modal success
  const handleReviewModalSuccess = async (comments?: string) => {
    try {
      // If document is NOT posted to Zoho, we need to save as draft first
      if (!isPostedToZoho && !documentId) {
        // Ensure customer_name is included in the JSON
        const draftData = omitSupplyFields({
          ...debitNoteJson,
          ...(selectedCustomerName
            ? { customer_name: selectedCustomerName }
            : {}),
        });

        // Save as draft first to get the ID
        const savedDebitNote = await saveDebitNoteAsDraft(draftData);

        if (!savedDebitNote || !savedDebitNote.id) {
          showError(operationsError || "Failed to save debit note as draft");
          return;
        }

        const debitNoteId = savedDebitNote.id;

        // Now send the review request with the actual debit note ID
        const { createReviewRequest } = await import(
          "../../../../shared/utils/reviewWorkflow"
        );
        await createReviewRequest({
          module_code: MODULE_CODES.DEBIT_NOTE,
          action_code:
            pendingReviewAction?.actionCode || ACTION_CODES.PUSH_TO_ZOHO,
          entity_type: "debit_note",
          entity_id: debitNoteId,
          request_comments: comments || "Please review this debit note",
          change_details: pendingReviewAction?.changeDetails || undefined,
        });

        showSuccess("Debit note saved as draft and review requested");
      } else {
        // ReviewRequestModal already showed success toast
      }

      // Close the modal
      setIsReviewModalOpen(false);

      // Clear tracked changes after submitting review
      setTrackedChanges([]);

      // Update original document to current state
      if (debitNoteJson) {
        setOriginalDebitNoteJson(debitNoteJson);
      }

      // Navigate back to debit notes list
      navigate("/dashboard/debit");
    } catch (error: any) {
      console.error("Error handling review modal success:", error);
      showError(error.message || "Failed to process review request");
      // Keep modal open so user can see the error and try again
    }
  };

  const handleApproveAndPost = useCallback(async () => {
    // Run validation first
    if (!validateDebitNote()) {
      // ErrorBanner will display all validation errors
      // Scroll to error banner
      if (errorBannerRef.current) {
        errorBannerRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      return;
    }

    // Determine which action we're performing
    const isUpdateAfterPush = isPostedToZoho || isEditMode;
    const actionCode = isUpdateAfterPush
      ? ACTION_CODES.UPDATE_AFTER_PUSH
      : ACTION_CODES.PUSH_TO_ZOHO;
    const needsReview = isUpdateAfterPush
      ? needsReviewForUpdateAfterPush
      : needsReviewForPushToZoho;
    const hasPermission = isUpdateAfterPush
      ? canUpdateAfterPush
      : canPushToZoho;

    // Check permission
    if (!hasPermission) {
      const errorMessage = isUpdateAfterPush
        ? getUpdateAfterPostPermissionDeniedMessage("debit notes", accountingBackend)
        : getPushPermissionDeniedMessage("debit notes", accountingBackend);
      showError(errorMessage);
      return;
    }

    // Closed month: non-Admin/CFO cannot approve/update to Zoho directly; show confirm modal to submit for review
    if (isMonthClosed && !isAdminOrCFO) {
      const fieldChanges =
        trackedChanges.length > 0
          ? trackedChanges
          : getFieldChangesFromDebitNoteForm(debitNoteJson);
      setPendingReviewAction({
        action: () => {},
        changeDetails: {
          closed_month: true,
          action_types: [
            isUpdateAfterPush ? "update_after_push" : "push_to_zoho",
            ACTION_TYPE_MONTH_CLOSE,
          ],
          field_changes: fieldChanges,
          raw_json: omitSupplyFields(debitNoteJson ?? undefined),
          original_json: originalDebitNoteJson ?? undefined,
        },
        disableSubmit: false,
        actionCode,
      });
      setPendingClosedMonthSource("approve");
      setShowClosedMonthConfirmModal(true);
      return;
    }

    // Build the actual action function
    const executeAction = async () => {
      try {
        clearOperationsError();

        // If in edit mode (posted to zoho), always use updateZohoDebitNote
        if (isEditMode && documentId) {
          const success = await updateZohoDebitNote(
            parseInt(documentId),
            omitSupplyFields(debitNoteJson)
          );

          if (success) {
            showSuccess(getUpdatedInBackendSuccessMessage("Debit note", accountingBackend));
            navigate("/dashboard/debit");
          } else {
            showError(operationsError || getFailedUpdateInBackendMessage("debit note", accountingBackend));
          }
          return;
        }

        const documentStatus = debitNoteDocument?.status
          ? Number(debitNoteDocument.status)
          : null;
        const hasZohoId = debitNoteDocument?.zoho_id;

        // Logic based on zoho_id and status:
        // 1. If zoho_id exists and status is 1 (completed/approved) -> use updateZohoDebitNote
        // 2. If zoho_id exists and status is 0 or 2 (draft/under review) -> use approveDebitNote
        // 3. If no zoho_id -> use createZohoDebitNote (pushToZoho) for new debit notes
        //    OR use update endpoint for existing debit notes that need to be pushed

        // Ensure customer_name is included in the JSON
        const debitNoteData = omitSupplyFields({
          ...debitNoteJson,
          ...(selectedCustomerName
            ? { customer_name: selectedCustomerName }
            : {}),
        });

        let success = false;

        if (hasZohoId && documentStatus === 1) {
          // Already posted to Zoho and completed - update it
          success = await updateZohoDebitNote(
            parseInt(documentId),
            debitNoteData
          );
        } else if (
          hasZohoId &&
          (documentStatus === 0 || documentStatus === 2)
        ) {
          // Has zoho_id but status is draft/under review - approve it
          success = await createZohoDebitNote(
            debitNoteData,
            signedUrl || undefined,
            documentId ? parseInt(documentId) : undefined
          );
        } else if (!documentId || isManualCreation) {
          // No documentId or manual creation - create new debit note and push to Zoho
          success = await createZohoDebitNote(
            debitNoteData,
            signedUrl || undefined
          );
        } else {
          // Existing debit note without zoho_id - pass documentId to update existing draft
          success = await createZohoDebitNote(
            debitNoteData,
            signedUrl || undefined,
            parseInt(documentId)
          );
        }

        if (success) {
          setStatus("approved");
          showSuccess("Debit note approved and posted successfully");
          navigate("/dashboard/debit");
        } else {
          showError(operationsError || "Failed to approve and post debit note");
        }
      } catch (err: any) {
        showError(err.message || "Failed to approve and post debit note");
        setApiErrors(err);
      }
    };

    // Check if review is needed
    if (needsReview) {
      // Check if there are tracked changes
      if (trackedChanges.length === 0) {
        // No changes - show modal but disable submit
        const changeDetails: ChangeDetails = {
          reason:
            "No changes detected. Please make changes to the document before requesting a review.",
          field_changes: [],
          raw_json: omitSupplyFields(debitNoteJsonRef.current || debitNoteJson),
          action_types: [actionCode],
        };

        setPendingReviewAction({
          action: executeAction,
          changeDetails,
          disableSubmit: true,
          actionCode,
        });
        setIsReviewModalOpen(true);
        return;
      }

      // There are changes - show review modal with changes
      const changeDetails: ChangeDetails = {
        reason: isUpdateAfterPush
          ? getReviewRequestUpdateAfterPostReason("debit note", accountingBackend)
          : getReviewRequestPushReason("debit note", accountingBackend),
        field_changes: trackedChanges.length > 0 ? trackedChanges : [],
        raw_json: omitSupplyFields(debitNoteJsonRef.current || debitNoteJson),
        action_types: [actionCode],
      };

      setPendingReviewAction({
        action: executeAction,
        changeDetails,
        disableSubmit: false,
        actionCode,
      });
      setIsReviewModalOpen(true);
      return;
    }

    // No review needed, proceed directly
    await executeAction();
  }, [
    debitNoteJson,
    documentId,
    selectedCustomerName,
    validateDebitNote,
    createZohoDebitNote,
    updateZohoDebitNote,
    debitNoteDocument,
    signedUrl,
    showSuccess,
    showError,
    navigate,
    clearOperationsError,
    operationsError,
    setApiErrors,
    isManualCreation,
    isEditMode,
    isPostedToZoho,
    canUpdateAfterPush,
    canPushToZoho,
    needsReviewForUpdateAfterPush,
    needsReviewForPushToZoho,
    trackedChanges,
    getFieldChangesFromDebitNoteForm,
    debitNoteJsonRef,
  ]);

  // Customer selection handler
  const handleCustomerSelect = useCallback(
    (customer: any) => {
      // Prevent selection if customer/vendor fields are disabled (e.g., when posted)
      if (isCustomerVendorDisabled) {
        return;
      }

      // CRITICAL: Clear all invoice-related state when customer changes
      setSelectedInvoiceId("");
      setSelectedInvoiceNumber("");
      resetInvoiceDetails();
      setLinkedInvoiceDate("");

      // Cache the selected customer
      selectedCustomerCacheRef.current = customer;

      setSelectedCustomerId(customer.zoho_customerid || "");
      setSelectedCustomerName(
        customer.customer_name || customer.company_name || ""
      );

      const customerCountry = customer.country || "";
      const customerState =
        customerCountry === "IN" ? customer.state || "" : "";

      const updates: Partial<DebitNoteDataEntity> & {
        reference_invoice_id?: string | undefined;
      } = {
        customer_id: customer.zoho_customerid || "",
        customer_name: customer.customer_name || customer.company_name || "",
        gst_treatment: (customer.gst_treatment || "") as any,
        gst_no: customer.gstin_vat_tax_id || "",
        country: customerCountry,
        state: customerState,
        reference_invoice_id: undefined,
        linked_invoice_id: undefined,
        zoho_invoiceid: undefined,
        reference_number: "",
        line_items: [],
      };

      // Auto-populate currency if customer has one.
      if (customer.currency_id) {
        const customerCurrency = findCurrencyById(customer.currency_id);
        if (customerCurrency) {
          handleCurrencyChange(customer.currency_id);
        }
      }

      updateDebitNoteJsonWithTracking(updates);

      // Clear customer error when customer is selected
      clearFieldError("customer_id");
      clearFieldError("linked_invoice_id");
      clearFieldError("country");
      clearFieldError("state");
    },
    [
      updateDebitNoteJsonWithTracking,
      findCurrencyById,
      handleCurrencyChange,
      clearFieldError,
      isCustomerVendorDisabled,
      resetInvoiceDetails,
      setSelectedInvoiceId,
      setSelectedInvoiceNumber,
      setLinkedInvoiceDate,
    ]
  );

  // Invoice selection handler - mandatory
  const handleInvoiceSelect = useCallback(
    async (invoice: any) => {
      // Cache the selected invoice
      selectedInvoiceCacheRef.current = invoice;

      const invoiceId = invoice.invoice_id || invoice.zoho_invoiceid || "";
      const invoiceNumber = invoice.invoice_number || "";
      const zohoInvoiceId = invoice.zoho_invoiceid || invoice.invoice_id || "";
      const invoiceDate = invoice.date || "";

      setSelectedInvoiceId(invoiceId);
      setSelectedInvoiceNumber(invoiceNumber);

      // Set the date from invoice if available
      if (invoiceDate) {
        setLinkedInvoiceDate(invoiceDate);
      }

      // Update debit note JSON with reference_invoice_id at top level
      const updates: any = {
        reference_invoice_id: invoiceId,
        linked_invoice_id: invoiceId,
        zoho_invoiceid: zohoInvoiceId,
        reference_number: invoiceNumber,
      };

      // Set the date from invoice if available (always auto-populate)
      if (invoiceDate) {
        updates.date = invoiceDate;
        clearFieldError("date");
      }

      updateDebitNoteJsonWithTracking(updates);

      // Clear linked invoice error when invoice is selected
      clearFieldError("linked_invoice_id");

      // Fetch invoice details to get line items
      // Check if we already have the invoice details for this invoice ID
      if (zohoInvoiceId) {
        // If invoiceDetails already has data for this invoice, skip fetching
        if (invoiceDetails && invoiceDetails.invoice_id === zohoInvoiceId) {
          // Already have the data, line items will be processed by useEffect
          return;
        }

        try {
          clearInvoiceDetailsError();
          await fetchInvoiceDetails(zohoInvoiceId);
        } catch (error) {
          console.error("Error fetching invoice details:", error);
          showError("Failed to fetch invoice details. Please try again.");
        }
      }
    },
    [
      fetchInvoiceDetails,
      clearFieldError,
      invoiceDetails,
      clearInvoiceDetailsError,
      showError,
      updateDebitNoteJson,
    ]
  );

  // Do NOT transform invoice line items - users must add line items manually

  const handleAddLineItem = useCallback(() => {
    const newItem: DebitNoteLineItemEntity = {
      description: "",
      quantity: 1,
      rate: 0,
      type: 1,
    };
    setEditingLineItemIndex(-1);
    setEditingLineItemData(newItem);
    setOriginalAccountName("");
    setIsEditLineItemModalOpen(true);
  }, []);

  const handleEditLineItem = useCallback(
    (index: number) => {
      setEditingLineItemIndex(index);
      const lineItemData = { ...lineItems[index] };
      setOriginalAccountName(lineItemData.account_name || "");
      setEditingLineItemData(lineItemData);
      setIsEditLineItemModalOpen(true);
    },
    [lineItems]
  );

  const handleDeleteLineItem = useCallback(
    (index: number) => {
      const updatedItems = lineItems.filter((_, i) => i !== index);

      // Track line item deletion
      if (
        originalDebitNoteJson?.line_items &&
        originalDebitNoteJson.line_items[index]
      ) {
        const deletedItem = originalDebitNoteJson.line_items[index];
        setTrackedChanges((prevTrackedChanges) => {
          const updatedChanges = prevTrackedChanges.filter(
            (change) => !change.field.startsWith(`line_items[${index}]`)
          );

          // Track that the line item was removed
          updatedChanges.push({
            field: `line_items[${index}].quantity`,
            old: _formatValueForTracking(deletedItem.quantity),
            new: "removed",
          });

          return updatedChanges;
        });
      }

      updateDebitNoteJson({
        line_items: updatedItems,
      });
    },
    [
      lineItems,
      updateDebitNoteJson,
      originalDebitNoteJson,
      _formatValueForTracking,
    ]
  );

  const handleSaveLineItem = useCallback(() => {
    if (editingLineItemData && debitNoteJson) {
      // For customer debit notes, use invoice_id in line items
      const zohoInvoiceId =
        debitNoteJson.zoho_invoiceid || debitNoteJson.linked_invoice_id || "";

      // Ensure tax_amount is calculated if missing (rounded to 2 decimals)
      const itemSubTotal = roundToTwoDecimals(
        (editingLineItemData.quantity || 0) * (editingLineItemData.rate || 0)
      );
      const calculatedTaxAmount = roundToTwoDecimals(
        (itemSubTotal * (editingLineItemData.tax_percentage || 0)) / 100
      );
      const taxAmount =
        editingLineItemData.tax_amount !== undefined &&
        editingLineItemData.tax_amount !== null
          ? roundToTwoDecimals(editingLineItemData.tax_amount)
          : calculatedTaxAmount;

      const updatedLineItem: DebitNoteLineItemEntity = {
        ...editingLineItemData,
        invoice_id: zohoInvoiceId,
        item_total: itemSubTotal,
        tax_amount: isGstEnabled ? taxAmount : 0,
      };

      let updatedItems: DebitNoteLineItemEntity[];

      if (editingLineItemIndex === -1) {
        updatedItems = [...lineItems, updatedLineItem];
      } else if (editingLineItemIndex !== null) {
        updatedItems = [...lineItems];
        updatedItems[editingLineItemIndex] = updatedLineItem;
      } else {
        updatedItems = lineItems;
      }

      // Track line item changes
      if (originalDebitNoteJson) {
        setTrackedChanges((prevTrackedChanges) => {
          const updatedChanges = prevTrackedChanges.filter((change) => {
            if (editingLineItemIndex === -1) {
              // For new items, don't filter (will add new entries)
              return true;
            } else if (editingLineItemIndex !== null) {
              // For editing, remove existing changes for this line item
              return !change.field.startsWith(
                `line_items[${editingLineItemIndex}]`
              );
            }
            return true;
          });
          const newChanges: Array<{
            field: string;
            old: string;
            new: string;
          }> = [];

          if (editingLineItemIndex === -1) {
            // New line item added
            const newIndex = updatedItems.length - 1;
            newChanges.push({
              field: `line_items[${newIndex}].quantity`,
              old: "empty",
              new: _formatValueForTracking(updatedLineItem.quantity),
            });
            newChanges.push({
              field: `line_items[${newIndex}].rate`,
              old: "empty",
              new: _formatValueForTracking(updatedLineItem.rate),
            });
          } else if (
            editingLineItemIndex !== null &&
            originalDebitNoteJson.line_items
          ) {
            // Editing existing line item - compare with original
            const originalLineItem =
              originalDebitNoteJson.line_items[editingLineItemIndex];

            if (originalLineItem) {
              // Track quantity change
              if (originalLineItem.quantity !== updatedLineItem.quantity) {
                newChanges.push({
                  field: `line_items[${editingLineItemIndex}].quantity`,
                  old: _formatValueForTracking(originalLineItem.quantity),
                  new: _formatValueForTracking(updatedLineItem.quantity),
                });
              }

              // Track rate change
              if (originalLineItem.rate !== updatedLineItem.rate) {
                newChanges.push({
                  field: `line_items[${editingLineItemIndex}].rate`,
                  old: _formatValueForTracking(originalLineItem.rate),
                  new: _formatValueForTracking(updatedLineItem.rate),
                });
              }
            }
          }

          return [...updatedChanges, ...newChanges];
        });
      }

      updateDebitNoteJson({
        line_items: updatedItems,
      });

      setIsEditLineItemModalOpen(false);
      setEditingLineItemIndex(null);
      setEditingLineItemData(null);
      setOriginalAccountName("");
    }
  }, [
    editingLineItemData,
    editingLineItemIndex,
    lineItems,
    debitNoteJson,
    updateDebitNoteJson,
    roundToTwoDecimals,
    isGstEnabled,
    originalDebitNoteJson,
    _formatValueForTracking,
  ]);

  const handleCancelEdit = () => {
    setIsEditLineItemModalOpen(false);
    setEditingLineItemIndex(null);
    setEditingLineItemData(null);
    setOriginalAccountName("");
  };

  // Query handlers
  const handleQueryClick = (queryId: number) => {
    setSelectedQueryId(queryId);
    setIsQueryDetailModalOpen(true);
  };

  const handleQueryRaiseModalOpen = () => {
    setIsQueryRaiseModalOpen(true);
  };

  // Check if there are any unresolved queries
  const hasUnresolvedQueries = queriesEntities.some(
    (q: any) => q.status !== "resolved"
  );
  const unresolvedCount = queriesEntities.filter(
    (q: any) => q.status !== "resolved"
  ).length;

  const handleLineItemDataChange = (newData: any) => {
    setEditingLineItemData(newData);
  };

  // Format amount helper
  const formatAmount = useCallback((amount: number) => {
    return `${amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }, []);

  // Helper function to format date as "March 15, 2024"
  const formatDate = useCallback(
    (
      dateString: string | null | undefined,
      fallback: string = "Not provided"
    ): string => {
      if (!dateString || dateString === "Not provided") {
        return fallback;
      }

      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return fallback;
        }

        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      } catch (error) {
        return fallback;
      }
    },
    []
  );

  // Show loading state during initial load
  if (isInitialLoading) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full flex-col items-center justify-center bg-default-background">
          <Loader />
          <span className="mt-4 text-body font-body text-subtext-color">
            Loading debit note...
          </span>
        </div>
      </DefaultPageLayout>
    );
  }

  // Show error state
  if (error) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full flex-col items-start bg-default-background px-6 py-6">
          <Alert variant="error" title="Error Loading Debit Note">
            {error}
          </Alert>
          <Button
            variant="neutral-secondary"
            onClick={() => navigate("/dashboard/debit")}
            className="mt-4"
          >
            Back to Debit Notes
          </Button>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start bg-default-background">
        {/* Header */}
        <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border bg-default-background px-6 py-4 flex-wrap">
          <div className="flex items-center gap-4">
            <Button
              variant="neutral-secondary"
              icon={<FeatherArrowLeft />}
              onClick={() => navigate(-1)}
            >
              Go Back
            </Button>
            <Breadcrumbs>
              {isManualCreation ? (
                <Breadcrumbs.Item onClick={() => navigate(-1)}>
                  Create Debit Note
                </Breadcrumbs.Item>
              ) : (
                <Breadcrumbs.Item onClick={() => navigate(-1)}>
                  Review Debit Note
                </Breadcrumbs.Item>
              )}
            </Breadcrumbs>
          </div>
          <div className="flex items-center gap-2">
            {!disableAutoSave && isAutoSaving && (
              <span className="text-caption font-caption text-subtext-color">
                Auto-saving...
              </span>
            )}
            {!disableAutoSave && lastSaved && (
              <span className="text-caption font-caption text-subtext-color">
                Last saved: {lastSaved.toLocaleTimeString()}
              </span>
            )}
            {/* Show Save as Draft only for manual creation */}
            {isManualCreation && !isPostedToZoho && (
              <Button
                variant="neutral-secondary"
                icon={<FeatherSave />}
                onClick={handleSaveAsDraft}
                disabled={
                  isReadOnly ||
                  operationsLoading ||
                  !canCreate ||
                  isDataLoading ||
                  isReviewPending
                }
              >
                Save as Draft
              </Button>
            )}
            {/* Hide Reject for edit mode (posted to zoho) or manual creation */}
            {!isEditMode && !isManualCreation && !isPostedToZoho && (
              <Button
                variant="destructive-primary"
                icon={<FeatherX />}
                onClick={handleReject}
                disabled={isReadOnly || operationsLoading || isReviewPending}
              >
                Reject
              </Button>
            )}
            <div className="flex items-center gap-2">
              <Button
                icon={<FeatherCheck />}
                onClick={handleApproveAndPost}
                disabled={
                  isReadOnly ||
                  operationsLoading ||
                  hasUnresolvedQueries ||
                  isDataLoading ||
                  isReviewPending ||
                  (isEditMode ? !canUpdateAfterPush : !canPushToZoho)
                }
              >
                {isEditMode ? getUpdateButtonLabel(accountingBackend) : "Approve & Post"}
              </Button>
            </div>
            {/* Hide Raise Query for manual creation */}
            {!isManualCreation && (
              <div className="flex items-center gap-2">
                <Button
                  variant="brand-secondary"
                  icon={<FeatherMessageCircle />}
                  onClick={handleQueryRaiseModalOpen}
                  disabled={isReadOnly || operationsLoading || isReviewPending}
                >
                  Raise a Query
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start overflow-y-auto">
          {/* Closed month banner - show when document is in a closed month */}
          {isMonthClosed === true && (
            <div className="w-full px-6 pt-4 pb-2">
              <ClosedMonthBanner entityLabel="Debit Note" />
            </div>
          )}

          {/* Review Status Banner - Show pending/rejected always, show approved temporarily */}
          {review && (
            <div className="w-full px-6 pt-4 pb-4">
              {(review.status === "pending" ||
                review.status === "rejected" ||
                (review.status === "approved" && showApprovedBanner)) && (
                <ReviewStatusBanner
                  review={review}
                  moduleCode={MODULE_CODES.DEBIT_NOTE}
                  currentUserId={user?.id ? parseInt(user.id, 10) : null}
                  onResubmit={() => {
                    if (review.status === "rejected") {
                      setIsReviewModalOpen(true);
                    }
                  }}
                />
              )}
            </div>
          )}

          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6 w-full">
            <div className="flex w-full items-start gap-6">
              {/* PDF Viewer - Only show if s3_bucket is not null */}
              {!isManualCreation &&
              (debitNoteDocument as any)?.s3_bucket !== null &&
              (debitNoteDocument as any)?.s3_bucket !== undefined &&
              (signedUrl || documentId) ? (
                <DocumentViewer
                  documentId={documentId ? parseInt(documentId) : undefined}
                  documentType="debit_note"
                  pdfUrl={signedUrl || undefined}
                  fileName={fileName || "credit-note.pdf"}
                  className="w-1/2"
                  onError={(error: any) => console.error("PDF Error:", error)}
                />
              ) : null}

              {/* Form Section */}
              <div
                className={`flex flex-col items-start gap-6 self-stretch px-6 py-6 ${
                  isManualCreation ||
                  (debitNoteDocument as any)?.s3_bucket === null ||
                  (debitNoteDocument as any)?.s3_bucket === undefined ||
                  !(signedUrl || documentId)
                    ? "w-full"
                    : "w-1/2"
                } overflow-y-auto`}
              >
                {/* Display validation errors with ErrorBanner component */}
                <div ref={errorBannerRef} className="w-full pb-2">
                  <ErrorBanner
                    errors={errors}
                    title="Please fix the following errors:"
                    autoScrollOnMount={true}
                    context="invoice"
                  />
                </div>

                {/* Show alert when there are unresolved queries */}
                {hasUnresolvedQueries && (
                  <Alert variant="warning" title="Unresolved Queries">
                    This debit note has {unresolvedCount} unresolved{" "}
                    {unresolvedCount === 1 ? "query" : "queries"}.{" "}
                    Please {getApproveAndPostQueriesMessage(accountingBackend)}
                  </Alert>
                )}

                {/* Note Summary */}
                <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6">
                  <div className="flex w-full items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-heading-3 font-heading-3 text-default-font">
                        Note Summary
                      </span>
                      <Badge variant="success">Debit Note</Badge>
                      <StatusBadgeForCreditAndDebitNotes
                        status={String(debitNoteDocument?.status || "0")}
                        type="debit"
                      />
                    </div>
                  </div>
                  <div className="flex w-full flex-col items-start gap-4">
                    {/* Row 1: Reference No and Date side by side (when no PDF or manual creation) */}
                    {isManualCreation ||
                    !(debitNoteDocument as any)?.s3_bucket ||
                    (debitNoteDocument as any)?.s3_bucket === null ||
                    (debitNoteDocument as any)?.s3_bucket === undefined ? (
                      <div className="flex w-full items-start gap-4">
                        <div className="flex grow shrink-0 basis-0 flex-col gap-1">
                          <TextField
                            className="h-auto w-full"
                            label={
                              <>
                                Reference No{" "}
                                <span className="text-error-600">*</span>
                              </>
                            }
                            helpText={errors.reference_number}
                            error={!!errors.reference_number}
                          >
                            <TextField.Input
                              placeholder="Select an invoice to auto-populate"
                              value={debitNoteJson?.reference_number || ""}
                              disabled={true}
                              readOnly={true}
                            />
                          </TextField>
                        </div>
                        <div className="flex grow shrink-0 basis-0 flex-col gap-1">
                          <TextField
                            className="h-auto w-full"
                            label={
                              <>
                                Date <span className="text-error-600">*</span>
                              </>
                            }
                            icon={<FeatherCalendar />}
                            helpText={
                              errors.date ||
                              (linkedInvoiceDate &&
                                !debitNoteJson?.date &&
                                "This will be auto-populated based on the selected invoice date")
                            }
                            error={!!errors.date}
                          >
                            <TextField.Input
                              type="date"
                              placeholder="YYYY-MM-DD"
                              value={debitNoteJson?.date || ""}
                              onChange={(e) => {
                                updateDebitNoteJsonWithTracking({
                                  date: e.target.value,
                                });
                                clearFieldError("date");
                              }}
                              disabled={isOtherFieldsDisabled}
                            />
                          </TextField>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex w-full items-center gap-4">
                          <span className="w-32 flex-none text-body font-body text-subtext-color">
                            Reference No{" "}
                            <span className="text-error-600">*</span>
                          </span>
                          <TextField
                            className="h-auto grow shrink-0 basis-0"
                            label=""
                            helpText={errors.reference_number}
                            error={!!errors.reference_number}
                          >
                            <TextField.Input
                              placeholder="Select an invoice to auto-populate"
                              value={debitNoteJson?.reference_number || ""}
                              disabled={true}
                              readOnly={true}
                            />
                          </TextField>
                        </div>
                        <div className="flex w-full items-center gap-4">
                          <span className="w-32 flex-none text-body font-body text-subtext-color">
                            Date <span className="text-error-600">*</span>
                          </span>
                          <TextField
                            className="h-auto grow shrink-0 basis-0"
                            label=""
                            icon={<FeatherCalendar />}
                            helpText={
                              errors.date ||
                              (linkedInvoiceDate &&
                                !debitNoteJson?.date &&
                                "This will be auto-populated based on the selected invoice date")
                            }
                            error={!!errors.date}
                          >
                            <TextField.Input
                              type="date"
                              placeholder="YYYY-MM-DD"
                              value={debitNoteJson?.date || ""}
                              onChange={(e) => {
                                updateDebitNoteJsonWithTracking({
                                  date: e.target.value,
                                });
                                clearFieldError("date");
                              }}
                              disabled={isOtherFieldsDisabled}
                            />
                          </TextField>
                        </div>
                      </>
                    )}
                    {/* Row 2: Customer/Vendor and GSTIN side by side (when no PDF or manual creation) */}
                    {isManualCreation ||
                    !(debitNoteDocument as any)?.s3_bucket ||
                    (debitNoteDocument as any)?.s3_bucket === null ||
                    (debitNoteDocument as any)?.s3_bucket === undefined ? (
                      <div className="flex w-full items-start gap-4">
                        <div className="flex grow shrink-0 basis-0 flex-col gap-1">
                          <span className="text-caption-bold font-caption-bold text-default-font">
                            Customer <span className="text-error-600">*</span>
                          </span>
                          <SearchableDropdown
                            fetchItems={async (searchTerm, limit, offset) => {
                              return await fetchCustomers(
                                searchTerm,
                                limit,
                                offset,
                                selectedCustomerId ||
                                  debitNoteJson?.customer_id ||
                                  undefined,
                                selectedCustomerName ||
                                  debitNoteJson?.customer_name ||
                                  undefined
                              );
                            }}
                            trigger={
                              <Button
                                variant="neutral-secondary"
                                className="w-full justify-between h-8"
                                iconRight={<FeatherChevronDown />}
                                disabled={
                                  isCustomerVendorDisabled || customersLoading
                                }
                              >
                                {customersLoading
                                  ? "Loading customers..."
                                  : selectedCustomerName ||
                                    debitNoteJson?.customer_name ||
                                    "Select customer"}
                              </Button>
                            }
                            getSearchableText={(customer: CustomerListItem) =>
                              customer.customer_name ||
                              customer.company_name ||
                              ""
                            }
                            renderItem={(customer: CustomerListItem) =>
                              customer.customer_name ||
                              customer.company_name ||
                              ""
                            }
                            getItemKey={(customer: CustomerListItem) =>
                              customer.zoho_customerid ||
                              customer.id?.toString() ||
                              ""
                            }
                            onSelect={(customer: CustomerListItem) => {
                              handleCustomerSelect(customer);
                              clearFieldError("customer_id");
                            }}
                            selectedKey={
                              selectedCustomerId ||
                              debitNoteJson?.customer_id ||
                              ""
                            }
                            searchPlaceholder="Search customers..."
                            emptyMessage="No customers available"
                            noResultsMessage="No customers match your search"
                          />
                          {errors.customer_id && (
                            <span className="text-caption font-caption text-error-700">
                              {errors.customer_id}
                            </span>
                          )}
                        </div>
                        <div className="flex grow shrink-0 basis-0 flex-col gap-1">
                          <TextField
                            className="h-auto w-full"
                            label={
                              <>
                                GSTIN{" "}
                                {debitNoteJson?.customer_id &&
                                  debitNoteJson?.gst_treatment ===
                                    "business_gst" && (
                                    <span className="text-error-600">*</span>
                                  )}
                              </>
                            }
                            helpText={errors.gst_no}
                            error={!!errors.gst_no}
                          >
                            <TextField.Input
                              placeholder="Enter GSTIN"
                              value={debitNoteJson?.gst_no || ""}
                              onChange={(e) => {
                                updateDebitNoteJson({
                                  gst_no: e.target.value,
                                });
                                clearFieldError("gst_no");
                              }}
                              disabled={
                                isOtherFieldsDisabled ||
                                debitNoteJson?.gst_treatment !== "business_gst"
                              }
                            />
                          </TextField>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex w-full items-center gap-4">
                          <span className="w-32 flex-none text-body font-body text-subtext-color">
                            Customer <span className="text-error-600">*</span>
                          </span>
                          <div className="grow shrink-0 basis-0 flex flex-col gap-1">
                            <SearchableDropdown
                              fetchItems={async (searchTerm, limit, offset) => {
                                return await fetchCustomers(
                                  searchTerm,
                                  limit,
                                  offset
                                );
                              }}
                              trigger={
                                <Button
                                  variant="neutral-secondary"
                                  className="w-full justify-between h-8"
                                  iconRight={<FeatherChevronDown />}
                                  disabled={
                                    isCustomerVendorDisabled || customersLoading
                                  }
                                >
                                  {customersLoading
                                    ? "Loading customers..."
                                    : selectedCustomerName ||
                                      debitNoteJson?.customer_name ||
                                      "Select customer"}
                                </Button>
                              }
                              getSearchableText={(customer: CustomerListItem) =>
                                customer.customer_name ||
                                customer.company_name ||
                                ""
                              }
                              renderItem={(customer: CustomerListItem) =>
                                customer.customer_name ||
                                customer.company_name ||
                                ""
                              }
                              getItemKey={(customer: CustomerListItem) =>
                                customer.zoho_customerid ||
                                customer.id?.toString() ||
                                ""
                              }
                              onSelect={(customer: CustomerListItem) => {
                                handleCustomerSelect(customer);
                                clearFieldError("customer_id");
                              }}
                              selectedKey={
                                selectedCustomerId ||
                                debitNoteJson?.customer_id ||
                                ""
                              }
                              searchPlaceholder="Search customers..."
                              emptyMessage="No customers available"
                              noResultsMessage="No customers match your search"
                            />
                            {errors.customer_id && (
                              <span className="text-caption font-caption text-error-700">
                                {errors.customer_id}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex w-full items-center gap-4">
                          <span className="w-32 flex-none text-body font-body text-subtext-color">
                            GSTIN{" "}
                            {debitNoteJson?.customer_id &&
                              debitNoteJson?.gst_treatment ===
                                "business_gst" && (
                                <span className="text-error-600">*</span>
                              )}
                          </span>
                          <TextField
                            className="h-auto grow shrink-0 basis-0"
                            label=""
                            helpText={errors.gst_no}
                            error={!!errors.gst_no}
                          >
                            <TextField.Input
                              placeholder="Enter GSTIN"
                              value={debitNoteJson?.gst_no || ""}
                              onChange={(e) => {
                                updateDebitNoteJson({
                                  gst_no: e.target.value,
                                });
                                clearFieldError("gst_no");
                              }}
                              disabled={
                                isOtherFieldsDisabled ||
                                debitNoteJson?.gst_treatment !== "business_gst"
                              }
                            />
                          </TextField>
                        </div>
                      </>
                    )}
                    {/* Row 3: Country and State side by side (when no PDF or manual creation) */}
                    {isManualCreation ||
                    !(debitNoteDocument as any)?.s3_bucket ||
                    (debitNoteDocument as any)?.s3_bucket === null ||
                    (debitNoteDocument as any)?.s3_bucket === undefined ? (
                      <div className="flex w-full items-start gap-4">
                        <div className="flex grow shrink-0 basis-0 flex-col gap-1">
                          <span className="text-caption-bold font-caption-bold text-default-font">
                            Country <span className="text-error-600">*</span>
                          </span>
                          <SearchableDropdown
                            items={COUNTRIES}
                            disabled={true}
                            trigger={
                              <Button
                                variant="neutral-secondary"
                                className="w-full justify-between h-8"
                                iconRight={<FeatherChevronDown />}
                                disabled={true}
                              >
                                {debitNoteJson?.country
                                  ? findCountryByCode(debitNoteJson.country)
                                      ?.name || "Select country"
                                  : "Select country"}
                              </Button>
                            }
                            getSearchableText={(country) => country.name}
                            renderItem={(country) => country.name}
                            getItemKey={(country) => country.code}
                            onSelect={() => {}}
                            selectedKey={debitNoteJson?.country || ""}
                            searchPlaceholder="Search countries..."
                            emptyMessage="No countries available"
                            noResultsMessage="No countries match your search"
                          />
                          {errors.country && (
                            <span className="text-caption font-caption text-error-700">
                              {errors.country}
                            </span>
                          )}
                        </div>
                        <div className="flex grow shrink-0 basis-0 flex-col gap-1">
                          <span className="text-caption-bold font-caption-bold text-default-font">
                            State{" "}
                            {debitNoteJson?.country === "IN" && (
                              <span className="text-error-600">*</span>
                            )}
                          </span>
                          <SearchableDropdown
                            items={INDIAN_STATES}
                            disabled={true}
                            trigger={
                              <Button
                                variant="neutral-secondary"
                                className="w-full justify-between h-8"
                                iconRight={<FeatherChevronDown />}
                                disabled={true}
                              >
                                {debitNoteJson?.state
                                  ? findIndianStateByCode(debitNoteJson.state)
                                      ?.name || debitNoteJson.state
                                  : "Select state"}
                              </Button>
                            }
                            getSearchableText={(state) => state.name}
                            renderItem={(state) => state.name}
                            getItemKey={(state) => state.code}
                            onSelect={() => {}}
                            selectedKey={debitNoteJson?.state || ""}
                            searchPlaceholder="Search states..."
                            emptyMessage="No states available"
                            noResultsMessage="No states match your search"
                          />
                          {errors.state && (
                            <span className="text-caption font-caption text-error-700">
                              {errors.state}
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex w-full items-center gap-4">
                          <span className="w-32 flex-none text-body font-body text-subtext-color">
                            Country <span className="text-error-600">*</span>
                          </span>
                          <div className="grow shrink-0 basis-0 flex flex-col gap-1">
                            <SearchableDropdown
                              items={COUNTRIES}
                              disabled={true}
                              trigger={
                                <Button
                                  variant="neutral-secondary"
                                  className="w-full justify-between h-8"
                                  iconRight={<FeatherChevronDown />}
                                  disabled={true}
                                >
                                  {debitNoteJson?.country
                                    ? findCountryByCode(debitNoteJson.country)
                                        ?.name || "Select country"
                                    : "Select country"}
                                </Button>
                              }
                              getSearchableText={(country) => country.name}
                              renderItem={(country) => country.name}
                              getItemKey={(country) => country.code}
                              onSelect={() => {}}
                              selectedKey={debitNoteJson?.country || ""}
                              searchPlaceholder="Search countries..."
                              emptyMessage="No countries available"
                              noResultsMessage="No countries match your search"
                            />
                            {errors.country && (
                              <span className="text-caption font-caption text-error-700">
                                {errors.country}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex w-full items-center gap-4">
                          <span className="w-32 flex-none text-body font-body text-subtext-color">
                            State{" "}
                            {debitNoteJson?.country === "IN" && (
                              <span className="text-error-600">*</span>
                            )}
                          </span>
                          <div className="grow shrink-0 basis-0 flex flex-col gap-1">
                            <SearchableDropdown
                              items={INDIAN_STATES}
                              disabled={true}
                              trigger={
                                <Button
                                  variant="neutral-secondary"
                                  className="w-full justify-between h-8"
                                  iconRight={<FeatherChevronDown />}
                                  disabled={true}
                                >
                                  {debitNoteJson?.state
                                    ? findIndianStateByCode(debitNoteJson.state)
                                        ?.name || debitNoteJson.state
                                    : "Select state"}
                                </Button>
                              }
                              getSearchableText={(state) => state.name}
                              renderItem={(state) => state.name}
                              getItemKey={(state) => state.code}
                              onSelect={() => {}}
                              selectedKey={debitNoteJson?.state || ""}
                              searchPlaceholder="Search states..."
                              emptyMessage="No states available"
                              noResultsMessage="No states match your search"
                            />
                            {errors.state && (
                              <span className="text-caption font-caption text-error-700">
                                {errors.state}
                              </span>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                    {/* Currency field */}
                    {isManualCreation ||
                    !(debitNoteDocument as any)?.s3_bucket ||
                    (debitNoteDocument as any)?.s3_bucket === null ||
                    (debitNoteDocument as any)?.s3_bucket === undefined ? (
                      <div className="flex flex-col gap-1 max-w-md">
                        <span className="text-caption-bold font-caption-bold text-default-font">
                          Currency <span className="text-error-600">*</span>
                        </span>
                        <SearchableDropdown
                          items={currencies}
                          loading={currenciesLoading}
                          trigger={
                            <Button
                              variant="neutral-secondary"
                              className="h-8 w-full justify-between"
                              iconRight={<FeatherChevronDown />}
                              disabled={
                                isOtherFieldsDisabled || currenciesLoading
                              }
                            >
                              {currenciesLoading
                                ? "Loading currencies..."
                                : debitNoteJson?.currency_id
                                ? findCurrencyById(debitNoteJson.currency_id)
                                    ?.currency_name_formatted ||
                                  debitNoteJson.currency_code
                                : "Select currency"}
                            </Button>
                          }
                          getSearchableText={(currency) =>
                            currency.currency_name_formatted ||
                            currency.currency_name
                          }
                          renderItem={(currency) =>
                            currency.currency_name_formatted ||
                            currency.currency_name
                          }
                          getItemKey={(currency) => currency.currency_id}
                          onSelect={(currency) => {
                            handleCurrencyChange(currency.currency_id);
                            clearFieldError("currency_id");
                          }}
                          selectedKey={debitNoteJson?.currency_id || ""}
                          searchPlaceholder="Search currencies..."
                          emptyMessage="No currencies available"
                          noResultsMessage="No currencies match your search"
                        />
                        {errors.currency_id && (
                          <span className="text-caption font-caption text-error-700">
                            {errors.currency_id}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="flex w-full items-start gap-4">
                        <span className="w-32 flex-none text-body font-body text-subtext-color pt-2">
                          Currency <span className="text-error-600">*</span>
                        </span>
                        <div className="flex flex-col gap-1 max-w-md">
                          <SearchableDropdown
                            items={currencies}
                            loading={currenciesLoading}
                            trigger={
                              <Button
                                variant="neutral-secondary"
                                className="h-8 w-full justify-between"
                                iconRight={<FeatherChevronDown />}
                                disabled={
                                  isOtherFieldsDisabled || currenciesLoading
                                }
                              >
                                {currenciesLoading
                                  ? "Loading currencies..."
                                  : debitNoteJson?.currency_id
                                  ? findCurrencyById(debitNoteJson.currency_id)
                                      ?.currency_name_formatted ||
                                    debitNoteJson.currency_code
                                  : "Select currency"}
                              </Button>
                            }
                            getSearchableText={(currency) =>
                              currency.currency_name_formatted ||
                              currency.currency_name
                            }
                            renderItem={(currency) =>
                              currency.currency_name_formatted ||
                              currency.currency_name
                            }
                            getItemKey={(currency) => currency.currency_id}
                            onSelect={(currency) => {
                              handleCurrencyChange(currency.currency_id);
                              clearFieldError("currency_id");
                            }}
                            selectedKey={debitNoteJson?.currency_id || ""}
                            searchPlaceholder="Search currencies..."
                            emptyMessage="No currencies available"
                            noResultsMessage="No currencies match your search"
                          />
                          {errors.currency_id && (
                            <span className="text-caption font-caption text-error-700">
                              {errors.currency_id}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                  <div className="flex w-full flex-col items-start gap-3">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Invoice Mapping Mode
                    </span>
                    {/* Invoice selection for customer debit notes */}
                    {isManualCreation ||
                    !(debitNoteDocument as any)?.s3_bucket ||
                    (debitNoteDocument as any)?.s3_bucket === null ||
                    (debitNoteDocument as any)?.s3_bucket === undefined ? (
                      <div className="flex w-full flex-col gap-1 max-w-md">
                        <span className="text-caption-bold font-caption-bold text-default-font">
                          Linked Invoice{" "}
                          <span className="text-error-600">*</span>
                        </span>
                        <SearchableDropdown
                          fetchItems={async (searchTerm, limit, offset) => {
                            if (
                              !selectedCustomerId &&
                              !debitNoteJson?.customer_id
                            ) {
                              return { items: [], hasMore: false };
                            }
                            return await fetchInvoices(
                              selectedCustomerId ||
                                debitNoteJson?.customer_id ||
                                "",
                              searchTerm,
                              limit,
                              offset,
                              selectedInvoiceId ||
                                debitNoteJson?.linked_invoice_id ||
                                undefined,
                              selectedInvoiceNumber || undefined
                            );
                          }}
                          trigger={
                            <Button
                              variant="neutral-secondary"
                              className="w-full justify-between h-8"
                              iconRight={<FeatherChevronDown />}
                              disabled={
                                isReadOnly ||
                                invoicesLoading ||
                                invoiceDetailsLoading ||
                                (!selectedCustomerName &&
                                  !debitNoteJson?.customer_name)
                              }
                            >
                              {invoicesLoading || invoiceDetailsLoading
                                ? "Loading invoices..."
                                : selectedInvoiceNumber
                                ? selectedInvoiceNumber || ""
                                : "Select invoice"}
                            </Button>
                          }
                          getSearchableText={(invoice: InvoiceListItemEntity) =>
                            invoice.invoice_number || ""
                          }
                          renderItem={(invoice: InvoiceListItemEntity) =>
                            `${invoice.invoice_number || ""}${
                              invoice.date ? ` - ${invoice.date}` : ""
                            }${
                              invoice.balance !== undefined &&
                              invoice.balance !== null
                                ? ` - ${
                                    invoice.currency_symbol || ""
                                  }${invoice.balance.toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}`
                                : ""
                            }`
                          }
                          getItemKey={(invoice: InvoiceListItemEntity) =>
                            invoice.invoice_id || invoice.id?.toString() || ""
                          }
                          onSelect={(invoice: InvoiceListItemEntity) => {
                            handleInvoiceSelect(invoice);
                            clearFieldError("linked_invoice_id");
                          }}
                          selectedKey={
                            selectedInvoiceId ||
                            debitNoteJson?.linked_invoice_id ||
                            ""
                          }
                          searchPlaceholder="Search invoices..."
                          emptyMessage={
                            !selectedCustomerName &&
                            !debitNoteJson?.customer_name
                              ? "Select a customer first"
                              : "No invoices available"
                          }
                          noResultsMessage="No invoices match your search"
                        />
                        {errors.linked_invoice_id && (
                          <span className="text-caption font-caption text-error-700">
                            {errors.linked_invoice_id}
                          </span>
                        )}
                        {invoiceDetailsError && (
                          <span className="text-caption font-caption text-error-700">
                            {invoiceDetailsError}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="flex w-full items-center gap-4">
                        <span className="w-32 flex-none text-body font-body text-subtext-color">
                          Linked Invoice{" "}
                          <span className="text-error-600">*</span>
                        </span>
                        <div className="grow shrink-0 basis-0 flex flex-col gap-1">
                          <SearchableDropdown
                            fetchItems={async (searchTerm, limit, offset) => {
                              if (
                                !selectedCustomerId &&
                                !debitNoteJson?.customer_id
                              ) {
                                return { items: [], hasMore: false };
                              }
                              return await fetchInvoices(
                                selectedCustomerId ||
                                  debitNoteJson?.customer_id ||
                                  "",
                                searchTerm,
                                limit,
                                offset,
                                selectedInvoiceId ||
                                  debitNoteJson?.linked_invoice_id ||
                                  undefined,
                                selectedInvoiceNumber || undefined
                              );
                            }}
                            trigger={
                              <Button
                                variant="neutral-secondary"
                                className="w-full justify-between h-8"
                                iconRight={<FeatherChevronDown />}
                                disabled={
                                  isReadOnly ||
                                  invoicesLoading ||
                                  invoiceDetailsLoading ||
                                  (!selectedCustomerName &&
                                    !debitNoteJson?.customer_name)
                                }
                              >
                                {invoicesLoading || invoiceDetailsLoading
                                  ? "Loading invoices..."
                                  : selectedInvoiceNumber
                                  ? selectedInvoiceNumber || ""
                                  : "Select invoice"}
                              </Button>
                            }
                            getSearchableText={(
                              invoice: InvoiceListItemEntity
                            ) => invoice.invoice_number || ""}
                            renderItem={(invoice: InvoiceListItemEntity) =>
                              `${invoice.invoice_number || ""}${
                                invoice.date ? ` - ${invoice.date}` : ""
                              }${
                                invoice.balance !== undefined &&
                                invoice.balance !== null
                                  ? ` - ${
                                      invoice.currency_symbol || ""
                                    }${invoice.balance.toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}`
                                  : ""
                              }`
                            }
                            getItemKey={(invoice: InvoiceListItemEntity) =>
                              invoice.invoice_id || invoice.id?.toString() || ""
                            }
                            onSelect={(invoice: InvoiceListItemEntity) => {
                              handleInvoiceSelect(invoice);
                              clearFieldError("linked_invoice_id");
                            }}
                            selectedKey={
                              selectedInvoiceId ||
                              debitNoteJson?.linked_invoice_id ||
                              ""
                            }
                            searchPlaceholder="Search invoices..."
                            emptyMessage={
                              !selectedCustomerName &&
                              !debitNoteJson?.customer_name
                                ? "Select a customer first"
                                : "No invoices available"
                            }
                            noResultsMessage="No invoices match your search"
                          />
                          {errors.linked_invoice_id && (
                            <span className="text-caption font-caption text-error-700">
                              {errors.linked_invoice_id}
                            </span>
                          )}
                          {invoiceDetailsError && (
                            <span className="text-caption font-caption text-error-700">
                              {invoiceDetailsError}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Document Information */}
                <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6">
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    Document Information
                  </span>
                  {isManualCreation ||
                  !debitNoteDocument?.s3_bucket ||
                  (debitNoteDocument as any)?.s3_bucket === null ||
                  (debitNoteDocument as any)?.s3_bucket === undefined ? (
                    <div className="flex w-full items-start gap-4 flex-wrap">
                      <div className="flex grow shrink-0 basis-0 flex-col gap-1">
                        <TextField className="h-auto w-full" label="Reason">
                          <TextField.Input
                            placeholder="Enter reason (optional)"
                            value={debitNoteJson?.reason || ""}
                            onChange={(e) =>
                              updateDebitNoteJson({
                                reason: e.target.value,
                              })
                            }
                            disabled={isOtherFieldsDisabled}
                          />
                        </TextField>
                      </div>
                      <div className="flex grow shrink-0 basis-0 flex-col gap-1">
                        <TextField className="h-auto w-full" label="Narration">
                          <TextField.Input
                            placeholder="Enter narration (optional)"
                            value={
                              debitNoteJson?.narration ||
                              debitNoteJson?.notes ||
                              ""
                            }
                            onChange={(e) =>
                              updateDebitNoteJson({
                                narration: e.target.value,
                                notes: e.target.value,
                              })
                            }
                            disabled={isOtherFieldsDisabled}
                          />
                        </TextField>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex w-full items-start gap-4 flex-wrap">
                        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4">
                          <div className="flex w-full items-center gap-4">
                            <span className="w-32 flex-none text-body font-body text-subtext-color">
                              Reason
                            </span>
                            <TextField
                              className="h-auto grow shrink-0 basis-0"
                              label=""
                            >
                              <TextField.Input
                                placeholder="Enter reason (optional)"
                                value={debitNoteJson?.reason || ""}
                                onChange={(e) =>
                                  updateDebitNoteJson({
                                    reason: e.target.value,
                                  })
                                }
                                disabled={isOtherFieldsDisabled}
                              />
                            </TextField>
                          </div>
                        </div>
                      </div>
                      <div className="flex w-full items-center gap-4">
                        <span className="w-32 flex-none text-body font-body text-subtext-color">
                          Narration
                        </span>
                        <TextField
                          className="h-auto grow shrink-0 basis-0"
                          label=""
                        >
                          <TextField.Input
                            placeholder="Enter narration (optional)"
                            value={
                              debitNoteJson?.narration ||
                              debitNoteJson?.notes ||
                              ""
                            }
                            onChange={(e) =>
                              updateDebitNoteJson({
                                narration: e.target.value,
                                notes: e.target.value,
                              })
                            }
                            disabled={isOtherFieldsDisabled}
                          />
                        </TextField>
                      </div>
                    </>
                  )}
                  {/* <div className="flex w-full items-center gap-2 rounded-md bg-neutral-50 px-3 py-2">
                    <Avatar size="x-small" image="">
                      A
                    </Avatar>
                    <span className="text-caption font-caption text-subtext-color">
                      Last edited by Admin
                    </span>
                  </div> */}
                </div>
              </div>
            </div>

            {/* ZOUR-65: which GST registration this note belongs to.
                READ-ONLY, always. A note follows its parent document, and Zoho
                does NOT enforce that — it accepts a note on a different
                location silently and splits the tax the other way — so the
                server overwrites it with the parent's before pushing and this
                only reports the outcome. */}
            {hasMultipleLocations && (
              <div className="flex w-full flex-col items-start px-6 pb-6">
                <LocationPicker
                  locations={activeLocations}
                  value={debitNoteJson?.location_id ?? null}
                  lockedReason={
                    noteHasParent
                      ? "Follows the invoice this note is raised against."
                      : null
                  }
                  onChange={(locationId) =>
                    updateDebitNoteJsonWithTracking({ location_id: locationId })
                  }
                />
              </div>
            )}

            {showPreGstNotice && (
              <div className="flex w-full flex-col items-start px-6 pb-6">
                <GstPreRegistrationNotice
                  documentDate={
                    serverGstDecision?.effective_date ||
                    linkedInvoiceDate ||
                    invoiceDetails?.date ||
                    debitNoteJson?.date ||
                    ""
                  }
                  documentLabel="debit note"
                  gstEnabledFrom={serverGstDecision?.gst_enabled_from ?? undefined}
                />
              </div>
            )}

            {/* Line Items Section */}
            <div className="flex w-full flex-col items-start gap-6 px-6 pb-6">
              <div className="flex w-full flex-col items-end gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6">
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-heading-3 font-heading-3 text-default-font">
                      Line Items
                    </span>
                    <Badge variant="success" icon={<FeatherCheck />}>
                      {lineItems.length} Lines Extracted
                    </Badge>
                  </div>
                  <Button
                    variant="neutral-primary"
                    size="small"
                    icon={<FeatherPlus />}
                    onClick={handleAddLineItem}
                    disabled={isReadOnly || !debitNoteJson?.linked_invoice_id}
                  >
                    Add Line
                  </Button>
                </div>

                {/* Line Items Table */}
                <div className="flex w-full flex-col items-start rounded-md border border-solid border-neutral-border overflow-x-auto">
                  <div className="flex items-center gap-4 border-b border-solid border-neutral-border bg-default-background px-4 py-3 w-full text-caption-bold font-caption-bold text-subtext-color">
                    <div className="flex w-48 flex-none items-start">
                      <span className="text-body font-body text-default-font">
                        Description
                      </span>
                    </div>
                    <div className="flex w-48 flex-none items-start">
                      <span className="text-body font-body text-default-font">
                        GL Account
                      </span>
                    </div>
                    <div className="flex w-24 flex-none items-start">
                      <span className="text-body font-body text-default-font">
                        HSN Code
                      </span>
                    </div>
                    <div className="flex w-20 flex-none items-start">
                      <span className="text-body font-body text-default-font">
                        Qty
                      </span>
                    </div>
                    <div className="flex w-28 flex-none items-start">
                      <span className="text-body font-body text-default-font">
                        Rate
                      </span>
                    </div>
                    {isGstEnabled && (
                      <div className="flex w-20 flex-none items-start">
                        <span className="text-body font-body text-default-font">
                          GST
                        </span>
                      </div>
                    )}
                    <div className="flex w-32 flex-none items-start">
                      <span className="text-body font-body text-default-font">
                        Amount
                      </span>
                    </div>
                    <div className="flex flex-1 items-start justify-end">
                      <span className="text-body font-body text-default-font">
                        Actions
                      </span>
                    </div>
                  </div>

                  {/* Line Item Rows */}
                  {lineItems.length === 0 ? (
                    <div className="flex w-full items-center justify-center px-4 py-8">
                      <span className="text-body font-body text-subtext-color">
                        No line items. Click "Add Line" to add one.
                      </span>
                    </div>
                  ) : (
                    lineItems.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-4 border-b border-solid border-neutral-100 bg-white px-4 py-3 w-full"
                      >
                        <div className="flex w-48 flex-none items-center">
                          <span className="text-body-bold font-body-bold text-default-font">
                            {item.description || "No description"}
                          </span>
                        </div>
                        <div className="flex w-48 flex-none items-center gap-1">
                          <div className="inline-flex items-start rounded-md border border-solid border-success-100 bg-success-100 px-2 py-1 min-h-6">
                            <span className="text-caption font-caption text-success-800 whitespace-normal break-words leading-tight">
                              {item.account_name || "-"}
                            </span>
                          </div>
                        </div>
                        <div className="flex w-24 flex-none items-start">
                          <span className="text-body font-body text-default-font">
                            {item.hsn_or_sac || "N/A"}
                          </span>
                        </div>
                        <div className="flex w-20 flex-none items-start">
                          <span className="text-body font-body text-default-font">
                            {item.quantity || 0}
                          </span>
                        </div>
                        <div className="flex w-28 flex-none items-start">
                          <span className="text-body font-body text-default-font">
                            {debitNoteJson?.currency_symbol || "₹"}
                            {formatAmount(item.rate || 0)}
                          </span>
                        </div>
                        {isGstEnabled && (
                          <div className="flex w-20 flex-none items-start">
                            <span className="text-body font-body text-default-font">
                              {item.tax_percentage || 0}%
                            </span>
                          </div>
                        )}
                        <div className="flex w-32 flex-none items-start">
                          <span className="text-body-bold font-body-bold text-default-font">
                            {debitNoteJson?.currency_symbol || "₹"}
                            {(() => {
                              // Calculate line item total
                              // If item_total is stored, use it; otherwise calculate from qty * rate
                              const itemSubTotal =
                                item.item_total !== undefined
                                  ? item.item_total
                                  : (item.quantity || 0) * (item.rate || 0);

                              // If tax_amount is stored, use it; otherwise calculate from tax_percentage
                              const itemTaxAmount = isGstEnabled
                                ? item.tax_amount !== undefined
                                  ? item.tax_amount
                                  : (itemSubTotal *
                                      (item.tax_percentage || 0)) /
                                    100
                                : 0;

                              return formatAmount(itemSubTotal + itemTaxAmount);
                            })()}
                          </span>
                        </div>
                        <div className="flex flex-1 items-center gap-1 justify-end">
                          <IconButton
                            size="small"
                            icon={<FeatherX />}
                            onClick={(
                              event: React.MouseEvent<HTMLButtonElement>
                            ) => {
                              event.stopPropagation();
                              handleDeleteLineItem(index);
                            }}
                            disabled={isReadOnly}
                          />
                          <IconButton
                            size="small"
                            icon={<FeatherEdit2 />}
                            onClick={(
                              event: React.MouseEvent<HTMLButtonElement>
                            ) => {
                              event.stopPropagation();
                              handleEditLineItem(index);
                            }}
                            disabled={isReadOnly}
                          />
                        </div>
                      </div>
                    ))
                  )}
                  {/* Totals - Calculated from line items:
                      - Subtotal: sum of (qty * rate) for all line items
                      - GST: sum of tax_amount for all line items
                      - Total: Subtotal + GST */}
                  <div className="flex flex-col items-end gap-3 border-t border-solid border-neutral-200 bg-default-background px-4 py-4 w-full">
                    {/* Subtotal = sum of (qty * rate) */}
                    <div className="flex w-full max-w-[384px] items-center justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Subtotal
                      </span>
                      <span className="text-body-bold font-body-bold text-default-font">
                        {debitNoteJson?.currency_symbol || "₹"}
                        {formatAmount(debitNoteJson?.sub_total || 0)}
                      </span>
                    </div>
                    {/* GST = sum of tax_amount from all line items */}
                    {isGstEnabled && (
                      <div className="flex w-full max-w-[384px] items-center justify-between">
                        <span className="text-body font-body text-subtext-color">
                          GST
                        </span>
                        <span className="text-body-bold font-body-bold text-default-font">
                          {debitNoteJson?.currency_symbol || "₹"}
                          {formatAmount(debitNoteJson?.tax_amount || 0)}
                        </span>
                      </div>
                    )}
                    {/* Total = Subtotal + GST */}
                    <div className="flex w-full max-w-[384px] items-center justify-between border-t border-solid border-neutral-border pt-3">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Total Amount
                      </span>
                      <span className="text-heading-3 font-heading-3 text-default-font">
                        {debitNoteJson?.currency_symbol || "₹"}
                        {formatAmount(
                          (debitNoteJson?.sub_total || 0) +
                            (isGstEnabled ? debitNoteJson?.tax_amount || 0 : 0)
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Activity Log and Queries */}
          <div className="flex w-full items-start gap-6 px-6 py-6">
            {/* Show queries even if empty */}
            <QueriesListForDocument
              queries={queriesEntities}
              loading={queriesLoading}
              loadingMore={queriesLoadingMore}
              hasMore={queriesHasMore}
              onLoadMore={loadMoreQueries}
              onQueryClick={handleQueryClick}
            />

            {/* Activity Log - Show even if empty */}
            {documentId && (
              <div
                className={`flex flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4 ${
                  queriesEntities.length > 0 || queriesLoading
                    ? "w-1/2"
                    : "w-full"
                }`}
              >
                <ActivityLogComponent
                  key={activityLogRefreshKey}
                  feature="debit_note"
                  featureId={documentId}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Line Item Modal */}
      <EditLineItemForDebitNoteComponent
        open={isEditLineItemModalOpen}
        onOpenChange={setIsEditLineItemModalOpen}
        editingLineItemData={editingLineItemData}
        editingLineItemIndex={editingLineItemIndex}
        originalAccountName={originalAccountName}
        taxes={[]}
        isNewItem={editingLineItemIndex === -1}
        isGstEnabled={isGstEnabled}
        isManualCreation={isManualCreation}
        hasLinkedDocument={!!debitNoteJson?.linked_invoice_id}
        customerName={selectedCustomerName || debitNoteJson?.customer_name}
        onSave={handleSaveLineItem}
        onCancel={handleCancelEdit}
        onLineItemDataChange={handleLineItemDataChange}
        debitNoteId={documentId}
      />

      {/* Query Raise Modal */}
      <QueryRaiseModal
        open={isQueryRaiseModalOpen}
        onOpenChange={setIsQueryRaiseModalOpen}
        sourceDoc={{
          type: "debit_note",
          id: parseInt(documentId || "0"),
          filename: fileName || "credit-note.pdf",
          amount:
            (debitNoteJson?.currency_symbol || "₹") +
            (debitNoteJson?.total || "0"),
          // IMPORTANT: Do not include viewUrl for debit_note - attachments are not supported for credit notes
        }}
        onQueryRaised={refetchQueries}
      />

      {/* Query Detail Modal */}
      <QueryDetailModal
        open={isQueryDetailModalOpen}
        onOpenChange={setIsQueryDetailModalOpen}
        queryId={selectedQueryId}
        onQueryResolved={refetchQueries}
        hideViewSourceButton={true}
      />

      {/* Closed month confirm modal - when non-Admin/CFO tries to save or approve in closed month */}
      <ClosedMonthConfirmModal
        open={showClosedMonthConfirmModal}
        onOpenChange={(open) => {
          setShowClosedMonthConfirmModal(open);
          if (!open) setPendingClosedMonthSource(null);
        }}
        entityLabel="Debit Note"
        onConfirm={() => {
          setShowClosedMonthConfirmModal(false);
          if (pendingClosedMonthSource !== "approve") {
            const fieldChanges =
              trackedChanges.length > 0
                ? trackedChanges
                : getFieldChangesFromDebitNoteForm(debitNoteJson);
            setPendingReviewAction({
              action: () => {},
              changeDetails: {
                closed_month: true,
                action_types: [
                  isPostedToZoho ? "update_after_push" : "edit",
                  ACTION_TYPE_MONTH_CLOSE,
                ],
                field_changes: fieldChanges,
                raw_json: omitSupplyFields(debitNoteJson ?? undefined),
                original_json: originalDebitNoteJson ?? undefined,
              },
              disableSubmit: false,
              actionCode: isPostedToZoho
                ? ACTION_CODES.UPDATE_AFTER_PUSH
                : ACTION_CODES.UPDATE,
            });
          }
          setPendingClosedMonthSource(null);
          setIsReviewModalOpen(true);
        }}
      />

      {/* Review Request Modal */}
      {(documentId || !isPostedToZoho) && pendingReviewAction && (
        <ReviewRequestModal
          open={isReviewModalOpen}
          onOpenChange={setIsReviewModalOpen}
          moduleCode={MODULE_CODES.DEBIT_NOTE}
          actionCode={
            pendingReviewAction.actionCode ||
            (isEditMode
              ? ACTION_CODES.UPDATE_AFTER_PUSH
              : ACTION_CODES.PUSH_TO_ZOHO)
          }
          entityType="debit_note"
          entityId={documentId ? parseInt(documentId) : 0}
          documentNumber={
            debitNoteJson?.debitnote_number || debitNoteJson?.debit_note_number
          }
          changeDetails={pendingReviewAction.changeDetails}
          disableSubmit={pendingReviewAction.disableSubmit}
          skipNavigation={true}
          skipReviewRequest={!isPostedToZoho && !documentId} // Skip if document is not posted and has no ID (need to save as draft first)
          onSuccess={handleReviewModalSuccess}
          onError={(error) => {
            console.error("Failed to create review request:", error);
            showError(error.message || "Failed to create review request");
          }}
        />
      )}
    </DefaultPageLayout>
  );
};

export default DebitNoteReviewPage;
